import { useState, useEffect, useMemo, useRef, useCallback, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// UI Components
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "./components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Badge } from "./components/ui/badge";
import { ScrollArea } from "./components/ui/scroll-area";
import { Separator } from "./components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./components/ui/tooltip";

// Lucide Icons
import { 
  LayoutDashboard, Users, UploadCloud, Settings, BadgeEuro, BarChart3, 
  Printer, Edit2, Trash2, CheckCircle2, AlertTriangle, Plus, LogOut,
  Info,
  ChevronLeft, ChevronRight, Building2, FileText, TrendingUp, TrendingDown,
  Target, Award, Menu, X, Eye, Save, RefreshCw, ChevronDown, ChevronUp,
  UserCircle, Home, Car, Wrench, Zap, FileSpreadsheet, Megaphone, 
  Scale, Plane, Mail, Shield, Receipt, Lock, Landmark, CircleDot, FolderOpen, CalendarDays, RotateCcw,
  Maximize2, Minimize2, ShieldCheck
} from "lucide-react";

import ViewerManagementPage from "./components/ViewerManagementPage";
import ViewerHome from "./components/ViewerHome";

// Client Navigation Context - provides both nav state and setter
const ClientNavContext = createContext({ nav: null, setNav: () => {} });

const useClientNav = () => useContext(ClientNavContext);

// Emoji-based icon wrapper for sidebar
const EmojiIcon = ({ emoji, className }) => <span className={`inline-flex items-center justify-center ${className || ''}`} style={{ fontSize: '1.1em', lineHeight: 1 }}>{emoji}</span>;

const CLIENT_NAV_ITEMS = [
  // --- SETUP ---
  { value: "_section_setup", label: "SETUP", section: true },
  { value: "account-config", label: "Configura Conti", icon: (p) => <EmojiIcon emoji="⚙️" {...p} /> },
  { value: "data-verification", label: "Verifica Dati", icon: (p) => <EmojiIcon emoji="✅" {...p} /> },
  { value: "editor", label: "Editor Dati", icon: (p) => <EmojiIcon emoji="✏️" {...p} /> },
  // --- ANALISI ECONOMICA ---
  { value: "_section_analisi", label: "ANALISI", section: true },
  { value: "overview", label: "Panoramica", icon: (p) => <EmojiIcon emoji="📊" {...p} /> },
  { value: "ce-annuale", label: "CE Annuale", icon: (p) => <EmojiIcon emoji="📑" {...p} /> },
  { value: "trend", label: "Trend Annuale", icon: (p) => <EmojiIcon emoji="📉" {...p} /> },
  { value: "costs", label: "Dettaglio Costi", icon: (p) => <EmojiIcon emoji="🧾" {...p} /> },
  { value: "analysis", label: "Analisi Economica", icon: (p) => <EmojiIcon emoji="📈" {...p} /> },
  { value: "comparison", label: "Confronti", icon: (p) => <EmojiIcon emoji="🔄" {...p} /> },
  // --- GESTIONE FINANZIARIA ---
  { value: "_section_finanza", label: "FINANZA", section: true },
  { value: "break-even", label: "Break Even", icon: (p) => <EmojiIcon emoji="⚖️" {...p} /> },
  { value: "cashflow", label: "Cash Flow", icon: (p) => <EmojiIcon emoji="💶" {...p} /> },
  { value: "commitments", label: "Impegni", icon: (p) => <EmojiIcon emoji="🏦" {...p} /> },
  { value: "depreciation-annual", label: "Ammortamenti", icon: (p) => <EmojiIcon emoji="📐" {...p} /> },
  { value: "balance-sheet", label: "SP Progressivo", icon: (p) => <EmojiIcon emoji="🏛️" {...p} /> },
  { value: "rendiconto", label: "Rendiconto Fin.", icon: (p) => <EmojiIcon emoji="🏦" {...p} /> },
  { value: "bank-rating", label: "Rating Bancario", icon: (p) => <EmojiIcon emoji="🏛️" {...p} /> },
  // --- PERFORMANCE ---
  { value: "_section_performance", label: "PERFORMANCE", section: true },
  { value: "kpi", label: "Indicatori", icon: (p) => <EmojiIcon emoji="🎯" {...p} /> },
  { value: "briefing", label: "Briefing", icon: (p) => <EmojiIcon emoji="📋" {...p} /> },
  { value: "ce-sede", label: "CE per Sede", icon: (p) => <EmojiIcon emoji="🏢" {...p} /> },
  { value: "marketing-roi", label: "Marketing ROI", icon: (p) => <EmojiIcon emoji="📣" {...p} /> },
  { value: "service-invoices", label: "Fatture & Servizi", icon: (p) => <EmojiIcon emoji="🧾" {...p} /> },
  { value: "product-margins", label: "Margine Prodotti", icon: (p) => <EmojiIcon emoji="🏆" {...p} /> },
  { value: "job-margins", label: "Margine Commesse", icon: (p) => <EmojiIcon emoji="📂" {...p} /> },
  { value: "whale-curve", label: "Whale Curve", icon: (p) => <EmojiIcon emoji="🐳" {...p} /> },
  { value: "clinic-kpi", label: "Analisi Operativa", icon: (p) => <EmojiIcon emoji="🩺" {...p} /> },
  // --- STRATEGIA ---
  { value: "_section_strategia", label: "STRATEGIA", section: true },
  { value: "salon-goals", label: "Obiettivo Titolare", icon: (p) => <EmojiIcon emoji="💇‍♀️" {...p} /> },
  { value: "tax-planning", label: "Pianif. Fiscale", icon: (p) => <EmojiIcon emoji="🛡️" {...p} /> },
  { value: "forfettario", label: "Forfettario", icon: (p) => <EmojiIcon emoji="🌿" {...p} />, requireForfettario: true },
  { value: "market-research", label: "Cosa cerca il cliente", icon: (p) => <EmojiIcon emoji="🔍" {...p} /> },
  { value: "potential-demand", label: "Domanda Potenziale", icon: (p) => <EmojiIcon emoji="📊" {...p} /> },
];

// Fixed cost subcategories with icons
const FIXED_COST_SUBCATEGORIES = {
  personnel: { label: 'Personale dipendente', icon: UserCircle },
  rent: { label: 'Locazione e spese condominiali', icon: Home },
  leasing: { label: 'Leasing e spese auto', icon: Car },
  equipment: { label: 'Attrezzature e software', icon: Settings },
  maintenance: { label: 'Manutenzioni', icon: Wrench },
  utilities: { label: 'Utenze', icon: Zap },
  admin: { label: 'Spese amministrative', icon: FileSpreadsheet },
  marketing: { label: 'Materiale pubblicitario e convegni', icon: Megaphone },
  consulting: { label: 'Consulenze tecniche e legali', icon: Scale },
  travel: { label: 'Alberghi e ristoranti', icon: Plane },
  postal: { label: 'Bolli e postali', icon: Mail },
  insurance: { label: 'Assicurazioni', icon: Shield },
  contributions: { label: 'Contributi associativi', icon: Receipt },
  nondeductible: { label: 'Costi indeducibili', icon: Lock },
  taxes: { label: 'Imposte, tasse e varie', icon: Landmark },
  security: { label: 'Vigilanza', icon: Shield },
  other: { label: 'Altri costi fissi', icon: CircleDot }
};

// Custom Components
import { YearlyReportTable } from "./components/YearlyReportTable";
import { BreakEvenPage } from "./components/BreakEvenPage";
import { BulkUploadDialog } from "./components/BulkUploadDialog";
import { CommitmentsTab } from "./components/CommitmentsTab";
import { KPIDashboard } from "./components/KPIDashboard";
import { BriefingPage } from "./components/BriefingPage";
import { CelebrationButton } from "./components/CelebrationButton";
import { SalonGoalsPage } from "./components/SalonGoalsPage";
import { CashFlowPage } from "./components/CashFlowPage";
import { CostsDetailSection } from "./components/CostsDetailSection";
import { ProductMarginPage } from "./components/ProductMarginPage";
import { WhaleCurvePage } from "./components/WhaleCurvePage";
import TaxPlanningPage from "./components/TaxPlanningPage";
import { LocationAnalysisPage } from "./components/LocationAnalysisPage";
import { MarketingROIPage } from "./components/MarketingROIPage";
import { ServiceInvoicesPage } from "./components/ServiceInvoicesPage";
import { MarketResearchPage } from "./components/MarketResearchPage";
import { PotentialDemandPage } from "./components/PotentialDemandPage";
import { DataVerificationPage } from "./components/DataVerificationPage";
import BalanceSheetProgressive from "./components/BalanceSheetProgressive";
import RendicontoFinanziario from "./components/RendicontoFinanziario";
import BankRatingPage from "./components/BankRatingPage";
import AccountConfiguration from "./components/AccountConfiguration";
import SPMappingDialog from "./components/SPMappingDialog";
import { JobProfitabilityPage } from "./components/JobProfitabilityPage";
import { AnnualDepreciationSection } from "./components/AnnualDepreciationSection";
import AccountConfigPage from "./components/AccountConfigPage";
import { ClinicKpiDashboard } from "./components/ClinicKpiDashboard";
import { DataHealthBanner } from "./components/DataHealthBanner";
import { CumulativeBilanciSection } from "./components/CumulativeBilanciSection";
import { FloatingAiChat } from "./components/FloatingAiChat";
import { ForfettarioPage } from "./components/ForfettarioPage";

// Recharts
import { 
  BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (e) {
      localStorage.removeItem("token");
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", res.data.token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.token}`;
    setToken(res.data.token);
    // Fetch full /me so we get role + accessible_clients (with allowed_pages)
    try {
      const me = await axios.get(`${API}/auth/me`);
      setUser(me.data);
      return { ...res.data, user: me.data };
    } catch (e) {
      setUser(res.data.user);
      return res.data;
    }
  };

  const register = async (email, password, name) => {
    const res = await axios.post(`${API}/auth/register`, { email, password, name });
    localStorage.setItem("token", res.data.token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.token}`;
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Utility functions
const formatCurrency = (value) => {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2
  }).format(value || 0);
};

const formatPercent = (value) => {
  return `${(value || 0).toFixed(1)}%`;
};

const getMonthName = (month) => {
  const months = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
    "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
  return months[month - 1] || "";
};

// Components
const Semaforo = ({ value, thresholds = { red: 0, yellow: 33, green: 66 } }) => {
  let color = "red";
  if (value >= thresholds.green) color = "green";
  else if (value >= thresholds.yellow) color = "yellow";

  return (
    <div className="flex gap-2 items-center">
      <div className={`semaforo-light ${color === "red" ? "active" : "inactive"}`} 
           style={{ backgroundColor: "#EF4444", color: "#EF4444" }} />
      <div className={`semaforo-light ${color === "yellow" ? "active" : "inactive"}`} 
           style={{ backgroundColor: "#F59E0B", color: "#F59E0B" }} />
      <div className={`semaforo-light ${color === "green" ? "active" : "inactive"}`} 
           style={{ backgroundColor: "#10B981", color: "#10B981" }} />
    </div>
  );
};

const Tachimetro = ({ value, max = 100, label, unit = "%", thresholds }) => {
  const clampedValue = Math.min(Math.max(value || 0, -max), max);
  const absMax = max;
  const normalized = ((clampedValue + absMax) / (2 * absMax)) * 100;
  const needleAngle = -90 + (normalized / 100) * 180;

  const th = thresholds || { red: 5, yellow: 15, green: 25 };
  const getColor = (val) => {
    if (val >= th.green) return "#059669";
    if (val >= th.yellow) return "#D97706";
    return "#DC2626";
  };
  const needleColor = getColor(value || 0);
  const statusLabel = value >= th.green ? "Ottimo" : value >= th.yellow ? "Attenzione" : "Critico";

  // Zone boundaries normalized 0-180 deg
  const zoneRedEnd = ((th.red + absMax) / (2 * absMax)) * 180;
  const zoneYellowEnd = ((th.yellow + absMax) / (2 * absMax)) * 180;
  const zoneGreenEnd = ((th.green + absMax) / (2 * absMax)) * 180;

  const polarToCart = (angleDeg, r, cx = 60, cy = 60) => {
    const rad = ((angleDeg - 180) * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  };

  const describeArc = (startDeg, endDeg, r, cx = 60, cy = 60) => {
    const s = polarToCart(startDeg, r, cx, cy);
    const e = polarToCart(endDeg, r, cx, cy);
    const large = endDeg - startDeg > 180 ? 1 : 0;
    return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`;
  };

  const ticks = [];
  for (let i = 0; i <= 6; i++) {
    const deg = (i / 6) * 180;
    const outer = polarToCart(deg, 47, 60, 60);
    const inner = polarToCart(deg, 42, 60, 60);
    const textPos = polarToCart(deg, 53, 60, 60);
    const tickVal = Math.round(-absMax + (i / 6) * 2 * absMax);
    ticks.push({ outer, inner, textPos, tickVal, deg });
  }

  const miniTicks = [];
  for (let i = 0; i <= 30; i++) {
    const deg = (i / 30) * 180;
    const outer = polarToCart(deg, 45, 60, 60);
    const inner = polarToCart(deg, 42, 60, 60);
    miniTicks.push({ outer, inner });
  }

  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative" style={{ width: 160, height: 95 }}>
        <svg viewBox="0 0 120 70" className="w-full h-full">
          <defs>
            <filter id={`shadow-${label}`}>
              <feDropShadow dx="0" dy="1" stdDeviation="1.5" floodOpacity="0.15" />
            </filter>
            <radialGradient id={`hub-${label}`} cx="50%" cy="40%" r="60%">
              <stop offset="0%" stopColor="#f8fafc" />
              <stop offset="100%" stopColor="#cbd5e1" />
            </radialGradient>
          </defs>

          {/* Outer ring background */}
          <path d={describeArc(0, 180, 48)} fill="none" stroke="#e2e8f0" strokeWidth="1" />

          {/* Fixed colored zone arcs - ALWAYS visible */}
          <path d={describeArc(0, zoneRedEnd, 44)} fill="none" stroke="#EF4444" strokeWidth="8" strokeLinecap="butt" opacity="0.7" />
          <path d={describeArc(zoneRedEnd, zoneYellowEnd, 44)} fill="none" stroke="#F59E0B" strokeWidth="8" strokeLinecap="butt" opacity="0.7" />
          <path d={describeArc(zoneYellowEnd, 180, 44)} fill="none" stroke="#10B981" strokeWidth="8" strokeLinecap="butt" opacity="0.7" />

          {/* Mini tick marks */}
          {miniTicks.map((t, i) => (
            <line key={`mt-${i}`} x1={t.outer.x} y1={t.outer.y} x2={t.inner.x} y2={t.inner.y} stroke="#94A3B8" strokeWidth="0.3" />
          ))}

          {/* Major tick marks */}
          {ticks.map((t, i) => (
            <g key={`tk-${i}`}>
              <line x1={t.outer.x} y1={t.outer.y} x2={t.inner.x} y2={t.inner.y} stroke="#475569" strokeWidth="0.8" />
              <text x={t.textPos.x} y={t.textPos.y} textAnchor="middle" dominantBaseline="middle" fontSize="4" fill="#64748B" fontWeight="500" fontFamily="system-ui">
                {t.tickVal}
              </text>
            </g>
          ))}

          {/* Needle */}
          <g style={{ transform: `rotate(${needleAngle}deg)`, transformOrigin: '60px 60px', transition: 'transform 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)' }}>
            <polygon points="59,22 61,22 60.5,60 59.5,60" fill={needleColor} filter={`url(#shadow-${label})`} />
            <line x1="60" y1="60" x2="60" y2="20" stroke={needleColor} strokeWidth="1.5" strokeLinecap="round" opacity="0.7" />
          </g>

          {/* Center hub */}
          <circle cx="60" cy="60" r="5" fill={`url(#hub-${label})`} stroke="#94A3B8" strokeWidth="0.5" filter={`url(#shadow-${label})`} />
          <circle cx="60" cy="60" r="2.5" fill={needleColor} opacity="0.8" />

          {/* Status badge */}
          <rect x={60 - 14} y="4" width="28" height="9" rx="4.5" fill={needleColor} opacity="0.12" />
          <text x="60" y="10" textAnchor="middle" fontSize="4.5" fill={needleColor} fontWeight="700" fontFamily="system-ui">{statusLabel}</text>
        </svg>
      </div>

      {/* Value + Label */}
      <div className="text-center -mt-1">
        <div className="text-2xl font-bold tabular-nums tracking-tight leading-none" style={{ color: needleColor }}>
          {value?.toFixed(1)}<span className="text-sm font-semibold ml-0.5">{unit}</span>
        </div>
        {label && (
          <div className="text-[11px] text-muted-foreground font-semibold tracking-widest uppercase mt-0.5">{label}</div>
        )}
      </div>
    </div>
  );
};

const KPICard = ({ title, value, trend, trendValue, trendLabel, icon: Icon, variant = "default" }) => {
  const isPositive = trendValue >= 0;
  const TrendIcon = isPositive ? TrendingUp : TrendingDown;
  const accentColors = {
    default: { bg: 'bg-slate-50', icon: 'bg-slate-100 text-slate-600', border: 'border-l-slate-400' },
  };
  const ac = accentColors[variant] || accentColors.default;

  return (
    <Card className="card-hover overflow-hidden border-l-[3px] border-l-slate-800">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className={`p-2.5 rounded-xl ${ac.icon}`}>
            <Icon className="h-5 w-5" />
          </div>
          {trend && trendValue != null && (
            <div className={`flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${
              isPositive ? "text-emerald-700 bg-emerald-50" : "text-red-700 bg-red-50"
            }`}>
              <TrendIcon className="h-3.5 w-3.5" />
              <span className="tabular-nums">{formatPercent(Math.abs(trendValue))}</span>
            </div>
          )}
        </div>
        <div>
          <div className="text-2xl font-extrabold tabular-nums tracking-tight">{value}</div>
          <div className="text-xs text-muted-foreground font-medium mt-0.5">{title}</div>
          {trendLabel && <div className="text-[10px] text-muted-foreground mt-1">{trendLabel}</div>}
        </div>
      </CardContent>
    </Card>
  );
};

// Login Page
const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let result;
      if (isLogin) {
        result = await login(email, password);
      } else {
        result = await register(email, password, name);
      }
      toast.success(isLogin ? "Accesso effettuato" : "Registrazione completata");
      const role = result?.user?.role || "admin";
      navigate(role === "client_viewer" ? "/viewer" : "/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-6">
              <BadgeEuro className="h-8 w-8 text-blue-600" />
              <span className="text-2xl font-bold">ControlloGestione</span>
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight">
              {isLogin ? "Bentornato" : "Crea account"}
            </h1>
            <p className="text-muted-foreground mt-2">
              {isLogin ? "Accedi al tuo portale di controllo di gestione" : "Inizia a gestire i tuoi clienti"}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <Label htmlFor="name">Nome</Label>
                <Input
                  id="name"
                  data-testid="register-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Il tuo nome"
                  required={!isLogin}
                />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@esempio.it"
                required
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>
            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? "Caricamento..." : (isLogin ? "Accedi" : "Registrati")}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm text-blue-600 hover:underline"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? "Non hai un account? Registrati" : "Hai già un account? Accedi"}
            </button>
          </div>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block flex-1 bg-slate-900 relative">
        <img
          src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200"
          alt="Financial Analysis"
          className="absolute inset-0 w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-12 text-white">
          <h2 className="text-3xl font-bold mb-4">Controllo di Gestione Professionale</h2>
          <p className="text-lg text-slate-300">
            Analisi economiche, margini, EBITDA, confronti e benchmark per i tuoi clienti.
          </p>
        </div>
      </div>
    </div>
  );
};

// Sidebar
// Returns the subset of CLIENT_NAV_ITEMS visible to the given user.
// - admin: full list (unchanged)
// - client_viewer: only sections with at least one allowed item, plus the
//   items whose `value` is in the per-client allowed_pages list. We always
//   keep "overview" as fallback if nothing is configured.
const filterNavItemsForRole = (items, user, clientId, fiscalRegime, businessType) => {
  // Mostra le tab "forfettario" se:
  //  - il cliente è già forfettario, OPPURE
  //  - è una Ditta Individuale / Professionista (potrebbe valutare il passaggio)
  const isForfettario = (fiscalRegime || '').startsWith('forfettario');
  const canEvaluateForfettario = businessType === 'ditta_individuale' || businessType === 'professionista';
  let baseItems = items.filter((it) => !it.requireForfettario || isForfettario || canEvaluateForfettario);

  if (!user || (user.role || "admin") === "admin") {
    // Apply section-pruning even for admin
    return _pruneEmptySections(baseItems);
  }
  const access = (user.accessible_clients || []).find((a) => a.client_id === clientId);
  const allowed = new Set(access?.allowed_pages || ["overview"]);
  const filtered = baseItems.filter((it) => it.section || allowed.has(it.value));
  return _pruneEmptySections(filtered);
};

const _pruneEmptySections = (filtered) => {
  const out = [];
  for (let i = 0; i < filtered.length; i++) {
    const it = filtered[i];
    if (it.section) {
      let hasChild = false;
      for (let j = i + 1; j < filtered.length; j++) {
        if (filtered[j].section) break;
        hasChild = true; break;
      }
      if (hasChild) out.push(it);
    } else {
      out.push(it);
    }
  }
  return out;
};

const Sidebar = ({ collapsed, setCollapsed }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { nav: clientNav } = useClientNav();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const defaultNavItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard" },
    { icon: Target, label: "Benchmark", path: "/benchmarks" },
    { icon: ShieldCheck, label: "Accessi Cliente", path: "/viewers", adminOnly: true },
  ].filter((it) => !it.adminOnly || (user?.role || "admin") === "admin");

  return (
    <aside className={`bg-slate-900 text-white flex-col transition-all duration-300 no-print hidden md:flex ${collapsed ? "w-16" : "w-56"}`}>
      <div className="p-4 flex flex-col items-center justify-center border-b border-slate-700">
        {!collapsed ? (
          <div className="text-center">
            <p className="text-lg font-black tracking-wide text-white leading-tight">SC</p>
            <p className="text-[13px] font-bold text-blue-400 leading-tight">Dottori Commercialisti</p>
            <div className="mt-1.5 h-px w-12 mx-auto bg-blue-500/40"></div>
            <p className="text-[9px] text-slate-500 mt-1 uppercase tracking-widest">Controllo di Gestione</p>
          </div>
        ) : (
          <span className="text-base font-black text-blue-400">SC</span>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-1 hover:bg-slate-800 rounded mt-2"
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
        </button>
      </div>

      {clientNav ? (
        /* Client detail navigation */
        <nav className="flex-1 flex flex-col overflow-hidden">
          {/* Back + client name */}
          <div className="p-2 border-b border-slate-700">
            {(user?.role || "admin") === "admin" ? (
              <button
                onClick={() => navigate("/clients")}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-800 transition-colors w-full text-slate-300 text-sm"
                data-testid="nav-back-clients"
              >
                <ChevronLeft className="h-4 w-4 shrink-0" />
                {!collapsed && <span>Clienti</span>}
              </button>
            ) : (user?.accessible_clients?.length || 0) > 1 ? (
              <button
                onClick={() => navigate("/viewer")}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-800 transition-colors w-full text-slate-300 text-sm"
                data-testid="nav-back-viewer"
              >
                <ChevronLeft className="h-4 w-4 shrink-0" />
                {!collapsed && <span>Le mie attività</span>}
              </button>
            ) : null}
            {!collapsed && clientNav.clientName && (
              <div className="px-3 py-1.5 text-xs font-semibold text-blue-400 truncate" title={clientNav.clientName}>
                {clientNav.clientName}
              </div>
            )}
          </div>
          {/* Section links */}
          <ScrollArea className="flex-1">
            <div className="p-2 space-y-0.5">
              {filterNavItemsForRole(CLIENT_NAV_ITEMS, user, clientNav?.clientId, clientNav?.fiscalRegime, clientNav?.businessType).map((item) => {
                if (item.section) {
                  return !collapsed ? (
                    <div key={item.value} className="pt-3 pb-1 px-3">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{item.label}</span>
                    </div>
                  ) : <div key={item.value} className="border-t border-slate-700 my-2" />;
                }
                const isActive = clientNav.activeTab === item.value;
                return (
                  <button
                    key={item.value}
                    onClick={() => clientNav.setActiveTab(item.value)}
                    className={`flex items-center gap-2.5 w-full px-3 py-2 rounded-lg transition-colors text-sm ${
                      isActive
                        ? "bg-blue-600/20 text-blue-400 font-medium"
                        : "text-slate-300 hover:bg-slate-800 hover:text-white"
                    }`}
                    data-testid={`nav-${item.value}`}
                  >
                    <item.icon className={`h-4 w-4 shrink-0 ${isActive ? "text-blue-400" : "text-slate-500"}`} />
                    {!collapsed && <span className="truncate">{item.label}</span>}
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        </nav>
      ) : (
        /* Default navigation */
        <nav className="flex-1 p-2">
          {defaultNavItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-800 transition-colors mb-1"
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="h-5 w-5 shrink-0" />
              {!collapsed && <span>{item.label}</span>}
            </Link>
          ))}
        </nav>
      )}

      <div className="p-4 border-t border-slate-700">
        {!collapsed && user && (
          <div className="mb-3 text-sm text-slate-400 truncate">{user.email}</div>
        )}
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-800 transition-colors w-full text-red-400"
          data-testid="logout-btn"
        >
          <LogOut className="h-5 w-5" />
          {!collapsed && <span>Esci</span>}
        </button>
      </div>
    </aside>
  );
};

// Layout
const AppLayout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [clientNav, setClientNav] = useState(null);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const ctxValue = useMemo(() => ({ nav: clientNav, setNav: setClientNav }), [clientNav]);

  const presentMode = clientNav?.presentMode || false;

  return (
    <ClientNavContext.Provider value={ctxValue}>
      <div className="min-h-screen flex bg-slate-50">
        {!presentMode && <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />}
        <main className="flex-1 overflow-auto pb-16 md:pb-0">{children}</main>
        {/* Mobile bottom nav */}
        <MobileBottomNav clientNav={clientNav} />
        {/* Floating AI assistant — admin only */}
        {isAdmin && !presentMode && <FloatingAiChat />}
      </div>
    </ClientNavContext.Provider>
  );
};

const MobileBottomNav = ({ clientNav }) => {
  const { user } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  if (!clientNav?.activeTab) return null;
  const role = user?.role || "admin";
  const access = role === "client_viewer"
    ? (user?.accessible_clients || []).find((a) => a.client_id === clientNav.clientId)
    : null;
  const allowed = role === "client_viewer" ? new Set(access?.allowed_pages || ["overview"]) : null;

  const adminQuickTabs = [
    { value: "overview", label: "Home", emoji: "📊" },
    { value: "ce-annuale", label: "CE", emoji: "📑" },
    { value: "kpi", label: "KPI", emoji: "🎯" },
    { value: "account-config", label: "Conti", emoji: "⚙️" },
  ];
  const viewerQuickTabsAll = [
    { value: "overview", label: "Home", emoji: "📊" },
    { value: "ce-annuale", label: "CE", emoji: "📑" },
    { value: "cashflow", label: "Cassa", emoji: "💶" },
    { value: "product-margins", label: "Prodotti", emoji: "🏆" },
    { value: "salon-goals", label: "Obiettivi", emoji: "🎯" },
    { value: "bank-rating", label: "Rating", emoji: "🏛️" },
  ];
  const quickTabs = role === "client_viewer"
    ? viewerQuickTabsAll.filter((t) => allowed.has(t.value)).slice(0, 4)
    : adminQuickTabs;
  const fullList = filterNavItemsForRole(CLIENT_NAV_ITEMS, user, clientNav.clientId, clientNav.fiscalRegime, clientNav.businessType);

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg md:hidden z-50" data-testid="mobile-nav">
      <div className="flex items-center justify-around h-14">
        {quickTabs.map(t => (
          <button
            key={t.value}
            onClick={() => { clientNav.setActiveTab(t.value); setShowMenu(false); }}
            className={`flex flex-col items-center gap-0.5 px-3 py-1 ${clientNav.activeTab === t.value ? "text-blue-600" : "text-slate-500"}`}
          >
            <span className="text-lg">{t.emoji}</span>
            <span className="text-[10px] font-medium">{t.label}</span>
          </button>
        ))}
        <button
          onClick={() => setShowMenu(!showMenu)}
          className={`flex flex-col items-center gap-0.5 px-3 py-1 ${showMenu ? "text-blue-600" : "text-slate-500"}`}
        >
          <span className="text-lg">{showMenu ? "✕" : "☰"}</span>
          <span className="text-[10px] font-medium">Menu</span>
        </button>
      </div>
      {showMenu && (
        <div className="absolute bottom-14 left-0 right-0 bg-white border-t shadow-2xl max-h-[60vh] overflow-y-auto p-3 grid grid-cols-3 gap-2">
          {fullList.filter(i => !i.section).map(item => (
            <button
              key={item.value}
              onClick={() => { clientNav.setActiveTab(item.value); setShowMenu(false); }}
              className={`flex flex-col items-center gap-1 p-2 rounded-lg text-xs ${
                clientNav.activeTab === item.value ? "bg-blue-50 text-blue-600 font-semibold" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              <span className="text-base"><item.icon className="h-4 w-4" /></span>
              <span className="text-center leading-tight">{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </nav>
  );
};

// Dashboard
const Dashboard = () => {
  const [clients, setClients] = useState([]);
  const [alerts, setAlerts] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const clientsRes = await axios.get(`${API}/clients-dashboard`);
      setClients(clientsRes.data);
      setLoading(false);
      // Load alerts in background - don't block the dashboard
      axios.get(`${API}/clients-dashboard-alerts`).then(res => setAlerts(res.data)).catch(() => {});
    } catch (e) {
      toast.error("Errore nel caricamento clienti");
      setLoading(false);
    }
  };

  const [multiUploadOpen, setMultiUploadOpen] = useState(false);
  const [multiUploading, setMultiUploading] = useState(false);
  const [multiResults, setMultiResults] = useState(null);
  const multiFileRef = useRef(null);

  const handleMultiUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setMultiUploading(true);
    setMultiResults(null);
    setMultiUploadOpen(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/multi-upload`, formData, { timeout: 120000 });
      setMultiResults(res.data);
      if (res.data.success_count > 0) {
        toast.success(`${res.data.success_count} aziende importate`);
        fetchClients();
      }
    } catch (err) {
      setMultiResults({ error: err.response?.data?.detail || err.message });
      toast.error("Errore nel caricamento multi-azienda");
    } finally {
      setMultiUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">I tuoi clienti</p>
        </div>
        <div className="flex gap-2">
          <label>
            <Button variant="outline" asChild data-testid="multi-upload-btn">
              <span><FolderOpen className="h-4 w-4 mr-2" /> Carica Multi-Azienda</span>
            </Button>
            <input ref={multiFileRef} type="file" accept=".pdf" onChange={handleMultiUpload} className="hidden" />
          </label>
          <Button onClick={() => navigate("/clients/new")} data-testid="new-client-btn">
            <Plus className="h-4 w-4 mr-2" /> Nuovo Cliente
          </Button>
        </div>
      </div>

      {/* Multi-upload results dialog */}
      {multiUploadOpen && (
        <Card className="mb-6 border-2 border-blue-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FolderOpen className="h-5 w-5" /> Caricamento Multi-Azienda
              <button onClick={() => setMultiUploadOpen(false)} className="ml-auto text-muted-foreground hover:text-foreground">✕</button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {multiUploading ? (
              <div className="py-6 text-center text-muted-foreground">Analisi e importazione in corso...</div>
            ) : multiResults?.error ? (
              <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{multiResults.error}</div>
            ) : multiResults ? (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Badge className="bg-slate-100 text-slate-700">{multiResults.companies_found} aziende trovate</Badge>
                  {multiResults.success_count > 0 && <Badge className="bg-emerald-100 text-emerald-700">{multiResults.success_count} importate</Badge>}
                  {multiResults.error_count > 0 && <Badge className="bg-red-100 text-red-700">{multiResults.error_count} errori</Badge>}
                </div>
                {multiResults.imported?.map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm p-2 bg-emerald-50 border border-emerald-200 rounded">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                    <span className="font-semibold">{r.company_name}</span>
                    <span className="text-muted-foreground">→</span>
                    <span className="text-blue-600 cursor-pointer hover:underline" onClick={() => navigate(`/clients/${r.client_id}`)}>{r.client_name}</span>
                    <span className="text-muted-foreground ml-auto">{r.month_name} {r.year} — {r.entries_count} voci — Ricavi: {new Intl.NumberFormat('it-IT', {style:'currency', currency:'EUR', maximumFractionDigits:0}).format(r.revenue)}</span>
                  </div>
                ))}
                {multiResults.errors?.map((e, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm p-2 bg-red-50 border border-red-200 rounded">
                    <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />
                    <span className="font-semibold">{e.company_name}</span>
                    <span className="text-red-600 ml-2">{e.error}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="text-center py-12">Caricamento...</div>
      ) : clients.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Nessun cliente</h3>
            <p className="text-muted-foreground mb-4">Inizia aggiungendo il tuo primo cliente</p>
            <Button onClick={() => navigate("/clients/new")}>
              <Plus className="h-4 w-4 mr-2" /> Aggiungi Cliente
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cliente</TableHead>
                <TableHead>Settore</TableHead>
                <TableHead>Ultima Analisi</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.map((client) => (
                <TableRow
                  key={client.id}
                  className="cursor-pointer hover:bg-slate-50"
                  data-testid={`dashboard-client-row-${client.id}`}
                >
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-3">
                      <div className="p-1.5 rounded-md bg-blue-100">
                        <Building2 className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <div className="font-semibold flex items-center gap-2">
                          {client.name}
                          {alerts[client.id]?.critical > 0 && (
                            <Badge className="bg-red-100 text-red-700 border-red-300 text-[10px] px-1.5" data-testid={`alert-critical-${client.id}`}>
                              {alerts[client.id].critical} critico
                            </Badge>
                          )}
                          {alerts[client.id]?.warning > 0 && !alerts[client.id]?.critical && (
                            <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-[10px] px-1.5" data-testid={`alert-warning-${client.id}`}>
                              {alerts[client.id].warning} attenzione
                            </Badge>
                          )}
                        </div>
                        {client.vat_number && <div className="text-xs text-muted-foreground">P.IVA: {client.vat_number}</div>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{client.sector}</Badge>
                  </TableCell>
                  <TableCell>
                    {client.last_analysis_date ? (
                      <div>
                        <div className="font-medium">{getMonthName(client.last_analysis_month)} {client.last_analysis_year}</div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(client.last_analysis_date).toLocaleDateString('it-IT')}
                        </div>
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">Nessuna analisi</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      onClick={() => navigate(`/clients/${client.id}`)}
                      data-testid={`open-client-${client.id}`}
                    >
                      <Eye className="h-4 w-4 mr-1" /> Apri
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
};

// Client List
const ClientList = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    name: "", sector: "Generale", business_type: "srl", fiscal_code: "", vat_number: "", notes: "",
    chart_of_accounts: "S1", business_locality: "locale", city: "", province: "", target_market: "b2c", action_radius: "", regime_contabile: "semplificata"
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/clients`);
      setClients(res.data);
    } catch (e) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.id}`, formData);
        toast.success("Cliente aggiornato");
      } else {
        await axios.post(`${API}/clients`, formData);
        toast.success("Cliente creato");
      }
      setShowDialog(false);
      setEditingClient(null);
      setFormData({ name: "", sector: "Generale", business_type: "srl", fiscal_code: "", vat_number: "", notes: "",
        chart_of_accounts: "S1", business_locality: "locale", city: "", province: "", target_market: "b2c", action_radius: "", regime_contabile: "semplificata", year_opened: null });
      fetchClients();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo cliente?")) return;
    try {
      await axios.delete(`${API}/clients/${id}`);
      toast.success("Cliente eliminato");
      fetchClients();
    } catch (e) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const openEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      sector: client.sector,
      business_type: client.business_type || "srl",
      fiscal_code: client.fiscal_code || "",
      vat_number: client.vat_number || "",
      notes: client.notes || "",
      chart_of_accounts: client.chart_of_accounts || "S1",
      business_locality: client.business_locality || "locale",
      city: client.city || "",
      province: client.province || "",
      target_market: client.target_market || "b2c",
      action_radius: client.action_radius || "",
      regime_contabile: client.regime_contabile || "semplificata",
      year_opened: client.year_opened || null,
      cash_business: client.cash_business ? 1 : 0
    });
    setShowDialog(true);
  };

  const openNew = () => {
    setEditingClient(null);
    setFormData({ name: "", sector: "Generale", business_type: "srl", fiscal_code: "", vat_number: "", notes: "",
      chart_of_accounts: "S1", business_locality: "locale", city: "", province: "", target_market: "b2c", action_radius: "", regime_contabile: "semplificata", year_opened: null });
    setShowDialog(true);
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Clienti</h1>
          <p className="text-muted-foreground">Gestione anagrafica clienti</p>
        </div>
        <Button onClick={openNew} data-testid="add-client-btn">
          <Plus className="h-4 w-4 mr-2" /> Nuovo Cliente
        </Button>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Settore</TableHead>
              <TableHead>P.IVA</TableHead>
              <TableHead>C.F.</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {clients.map((client) => (
              <TableRow key={client.id} data-testid={`client-row-${client.id}`}>
                <TableCell className="font-medium">{client.name}</TableCell>
                <TableCell>{client.sector}</TableCell>
                <TableCell>{client.vat_number || "-"}</TableCell>
                <TableCell>{client.fiscal_code || "-"}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => navigate(`/clients/${client.id}`)}
                      data-testid={`view-client-${client.id}`}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => openEdit(client)}
                      data-testid={`edit-client-${client.id}`}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => handleDelete(client.id)}
                      data-testid={`delete-client-${client.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader className="shrink-0">
            <DialogTitle>{editingClient ? "Modifica Cliente" : "Nuovo Cliente"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="client-name-input"
              />
            </div>
            <div>
              <Label>Settore</Label>
              <Select value={formData.sector} onValueChange={(v) => setFormData({ ...formData, sector: v })}>
                <SelectTrigger data-testid="client-sector-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Generale">Generale</SelectItem>
                  <SelectItem value="Manifatturiero">Manifatturiero</SelectItem>
                  <SelectItem value="Costruzioni">Costruzioni</SelectItem>
                  <SelectItem value="Commercio">Commercio</SelectItem>
                  <SelectItem value="Trasporto e Logistica">Trasporto e Logistica</SelectItem>
                  <SelectItem value="Alloggio e Ristorazione">Alloggio e Ristorazione</SelectItem>
                  <SelectItem value="Informatica e Comunicazione">Informatica e Comunicazione</SelectItem>
                  <SelectItem value="Professionisti">Professionisti</SelectItem>
                  <SelectItem value="Servizi alle Imprese">Servizi alle Imprese</SelectItem>
                  <SelectItem value="Servizi alla Persona">Servizi alla Persona</SelectItem>
                  <SelectItem value="Intrattenimento e Tempo Libero">Intrattenimento e Tempo Libero</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Forma Giuridica</Label>
              <Select value={formData.business_type} onValueChange={(v) => setFormData({ ...formData, business_type: v })}>
                <SelectTrigger data-testid="client-business-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="srl">SRL / SPA (Società di capitali)</SelectItem>
                  <SelectItem value="snc_sas">SNC / SAS (Società di persone)</SelectItem>
                  <SelectItem value="ditta_individuale">Ditta Individuale</SelectItem>
                  <SelectItem value="professionista">Professionista</SelectItem>
                </SelectContent>
              </Select>
              {(formData.business_type === "snc_sas" || formData.business_type === "ditta_individuale") && (
                <p className="text-xs text-violet-600 mt-1">
                  Potrai inserire il compenso soci/titolare nel CE Annuale per calcolare il risultato effettivo.
                </p>
              )}
            </div>
            <div>
              <Label>Regime Contabile</Label>
              <Select value={formData.regime_contabile || 'semplificata'} onValueChange={(v) => setFormData({ ...formData, regime_contabile: v })}>
                <SelectTrigger data-testid="client-regime-contabile-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ordinaria">Ordinaria (con Stato Patrimoniale)</SelectItem>
                  <SelectItem value="semplificata">Semplificata (senza SP)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Determina come viene calcolato il Rating Bancario.
              </p>
            </div>
            <div>
              <Label>Anno Apertura Attivita</Label>
              <Input type="number" placeholder="es. 2018" min="1900" max={new Date().getFullYear()}
                value={formData.year_opened || ""}
                onChange={(e) => setFormData({ ...formData, year_opened: e.target.value ? parseInt(e.target.value) : null })}
                data-testid="client-year-opened-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Usato per calcolare l'anzianita nel Rating Bancario.
              </p>
            </div>
            <div className="md:col-span-2">
              <label className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4"
                  checked={!!formData.cash_business}
                  onChange={(e) => setFormData({ ...formData, cash_business: e.target.checked ? 1 : 0 })}
                  data-testid="client-cash-business-toggle"
                />
                <div className="flex-1">
                  <div className="font-semibold text-sm">💵 Attività Cash — incassa subito</div>
                  <p className="text-xs text-slate-600 mt-1">
                    Spunta se il cliente incassa al momento del servizio (parrucchieri, estetiste,
                    bar, ristoranti, B2C senza fatturazione differita). Esclude il <strong>Quick Ratio</strong> dal Rating Bancario
                    e ridistribuisce il peso sulla Liquidità Corrente, evitando false penalizzazioni.
                  </p>
                </div>
              </label>
            </div>
            <div>{/* placeholder */}
              {(formData.business_type === "professionista" || formData.business_type === "ditta_individuale") && (
                <div className="mt-2 space-y-3 p-3 bg-violet-50 rounded-lg border border-violet-200">
                  <div>
                    <Label className="text-xs">Regime Fiscale</Label>
                    <Select value={formData.fiscal_regime || 'ordinario'} onValueChange={(v) => setFormData({ ...formData, fiscal_regime: v })}>
                      <SelectTrigger data-testid="client-fiscal-regime-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ordinario">Ordinario (IRPEF a scaglioni)</SelectItem>
                        <SelectItem value="forfettario_15">Forfettario (15%)</SelectItem>
                        <SelectItem value="forfettario_5">Forfettario startup (5%)</SelectItem>
                      </SelectContent>
                    </Select>
                    {formData.business_type === "ditta_individuale" && (
                      <p className="text-[10px] text-slate-500 mt-0.5">Forfettario disponibile fino a 85.000 € di ricavi/compensi annui.</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs">Cassa Previdenziale</Label>
                    <Select value={formData.pension_fund || 'gestione_separata'} onValueChange={(v) => setFormData({ ...formData, pension_fund: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gestione_separata">INPS Gestione Separata (26,07%)</SelectItem>
                        <SelectItem value="artigiani">INPS Artigiani (24% + minimale ~4.400 €)</SelectItem>
                        <SelectItem value="commercianti">INPS Commercianti (24,48% + minimale ~4.500 €)</SelectItem>
                        <SelectItem value="inarcassa">Inarcassa (Ingegneri/Architetti)</SelectItem>
                        <SelectItem value="cassa_forense">Cassa Forense (Avvocati)</SelectItem>
                        <SelectItem value="cassa_commercialisti">Cassa Dott. Commercialisti</SelectItem>
                        <SelectItem value="cassa_biologi">Cassa Biologi</SelectItem>
                        <SelectItem value="cassa_agronomi">Cassa Agronomi</SelectItem>
                        <SelectItem value="custom">Altra Cassa (imposto io la %)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.pension_fund === 'custom' && (
                    <div>
                      <Label className="text-xs">% Soggettivo</Label>
                      <Input type="number" step="0.01" value={formData.custom_pension_pct || ''} onChange={(e) => setFormData({ ...formData, custom_pension_pct: e.target.value })} placeholder="es. 14.5" />
                    </div>
                  )}
                  {(formData.fiscal_regime || '').startsWith('forfettario') && (
                    <div>
                      <Label className="text-xs">Coefficiente di redditivita (%)</Label>
                      <Input type="number" step="1" value={formData.profitability_coefficient || '78'} onChange={(e) => setFormData({ ...formData, profitability_coefficient: e.target.value })} placeholder="78" />
                      <p className="text-[10px] text-muted-foreground mt-0.5">78% per professionisti, 40% per commercio, 67% per servizi...</p>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Partita IVA</Label>
                <Input
                  value={formData.vat_number}
                  onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                  data-testid="client-vat-input"
                />
              </div>
              <div>
                <Label>Codice Fiscale</Label>
                <Input
                  value={formData.fiscal_code}
                  onChange={(e) => setFormData({ ...formData, fiscal_code: e.target.value })}
                  data-testid="client-cf-input"
                />
              </div>
            </div>
            <div>
              <Label>Piano dei Conti</Label>
              <Select value={formData.chart_of_accounts} onValueChange={(v) => setFormData({ ...formData, chart_of_accounts: v })}>
                <SelectTrigger data-testid="client-chart-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="S1">S1 (Standard)</SelectItem>
                  <SelectItem value="FLEX">Flessibile (03/04)</SelectItem>
                  <SelectItem value="GENERIC">Altro (classificazione AI)</SelectItem>
                </SelectContent>
              </Select>
              {formData.chart_of_accounts === "GENERIC" && (
                <p className="text-xs text-blue-600 mt-1">
                  I conti verranno classificati automaticamente con AI al primo caricamento del bilancio.
                </p>
              )}
            </div>
            <div className="border-t pt-3 mt-2">
              <p className="text-xs font-medium text-slate-500 mb-2">Domanda Potenziale</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tipo Business</Label>
                  <Select value={formData.business_locality} onValueChange={(v) => setFormData({ ...formData, business_locality: v })}>
                    <SelectTrigger data-testid="client-locality-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="locale">Locale</SelectItem>
                      <SelectItem value="online">Online</SelectItem>
                      <SelectItem value="misto">Misto</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Target</Label>
                  <Select value={formData.target_market} onValueChange={(v) => setFormData({ ...formData, target_market: v })}>
                    <SelectTrigger data-testid="client-target-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="b2c">Privati (B2C)</SelectItem>
                      <SelectItem value="b2b">Aziende (B2B)</SelectItem>
                      <SelectItem value="both">Entrambi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Città</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Es: Roma, Milano..."
                    data-testid="client-city-input"
                  />
                </div>
                <div>
                  <Label>Provincia</Label>
                  <Input
                    value={formData.province}
                    onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                    placeholder="Es: RM, MI..."
                    data-testid="client-province-input"
                  />
                </div>
                {(formData.business_locality === "locale" || formData.business_locality === "misto") && (
                  <div className="col-span-2">
                    <Label>Raggio d'azione</Label>
                    <Select value={formData.action_radius || ""} onValueChange={(v) => setFormData({ ...formData, action_radius: v })}>
                      <SelectTrigger data-testid="client-radius-select">
                        <SelectValue placeholder="Seleziona..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10km">10 km</SelectItem>
                        <SelectItem value="30km">30 km</SelectItem>
                        <SelectItem value="provinciale">Provinciale</SelectItem>
                        <SelectItem value="regionale">Regionale</SelectItem>
                        <SelectItem value="nazionale">Nazionale</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter className="shrink-0 border-t pt-4">
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annulla</Button>
            <Button onClick={handleSave} data-testid="save-client-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Entries Editor Component
const EntriesEditor = ({ clientId, periodId, entries, mappings: propMappings, onUpdate }) => {
  const [editedEntries, setEditedEntries] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newEntry, setNewEntry] = useState({ account_code: '', account_name: '', amount: 0, category: 'uncategorized', subcategory: '' });
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Build a lookup map from account_code -> mapping
  const mappingMap = useMemo(() => {
    const map = {};
    (propMappings || []).forEach(m => { map[m.account_code] = m; });
    return map;
  }, [propMappings]);

  // Get effective category for an entry (mapping overrides entry)
  const getEffective = (entry) => {
    const m = mappingMap[entry.account_code];
    return {
      category: m?.category || entry.category || 'uncategorized',
      subcategory: m?.subcategory || entry.subcategory || '',
      fromMapping: !!m && m.category && m.category !== 'uncategorized',
    };
  };

  const categories = [
    { value: 'revenue', label: 'Ricavi' },
    { value: 'variable_cost', label: 'Costo Variabile' },
    { value: 'fixed_cost', label: 'Costo Fisso' },
    { value: 'depreciation', label: 'Ammortamento' },
    { value: 'financial', label: 'Onere Finanziario' },
    { value: 'tax', label: 'Imposta' },
    { value: 'admin_compensation', label: 'Compenso Amministratore' },
    { value: 'brand_royalties', label: 'Royalties sul Marchio' },
    { value: 'exclude', label: 'Escludi (macroclasse)' },
  ];

  const categoryLabel = (val) => categories.find(c => c.value === val)?.label || 'Non classificato';

  const subcategories = Object.entries(FIXED_COST_SUBCATEGORIES).map(([key, val]) => ({
    value: key,
    label: val.label
  }));

  useEffect(() => {
    setEditedEntries([...entries]);
    setHasChanges(false);
  }, [entries]);

  const updateEntry = (index, field, value) => {
    const updated = [...editedEntries];
    updated[index] = { ...updated[index], [field]: value };
    setEditedEntries(updated);
    setHasChanges(true);
  };

  const deleteEntry = (index) => {
    const updated = editedEntries.filter((_, i) => i !== index);
    setEditedEntries(updated);
    setHasChanges(true);
  };

  const addEntry = () => {
    if (!newEntry.account_code || !newEntry.account_name) {
      toast.error("Compila codice e descrizione");
      return;
    }
    setEditedEntries([...editedEntries, { ...newEntry }]);
    setNewEntry({ account_code: '', account_name: '', amount: 0, category: 'uncategorized', subcategory: '' });
    setShowAddDialog(false);
    setHasChanges(true);
  };

  const saveChanges = async () => {
    if (!periodId) {
      toast.error("Nessun periodo selezionato");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/clients/${clientId}/periods/${periodId}/entries`, {
        entries: editedEntries
      });
      toast.success("Dati salvati");
      setHasChanges(false);
      onUpdate();
    } catch (e) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (!periodId) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">Nessun periodo selezionato</h3>
          <p className="text-muted-foreground">Carica un bilancio per modificare i dati</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Editor Dati Contabili</h3>
          <p className="text-sm text-muted-foreground">{editedEntries.length} voci</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowAddDialog(true)} data-testid="add-entry-btn">
            <Plus className="h-4 w-4 mr-2" /> Aggiungi Voce
          </Button>
          <Button onClick={saveChanges} disabled={!hasChanges || saving} data-testid="save-entries-btn">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Salvataggio..." : "Salva Modifiche"}
          </Button>
        </div>
      </div>

      {hasChanges && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <span className="text-sm text-yellow-800">Hai modifiche non salvate</span>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-28">Codice</TableHead>
                <TableHead>Descrizione</TableHead>
                <TableHead className="w-36">Importo</TableHead>
                <TableHead className="w-40">Categoria</TableHead>
                <TableHead className="w-48">Sottocategoria</TableHead>
                <TableHead className="w-20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {editedEntries.map((entry, index) => {
                const eff = getEffective(entry);
                return (
                <TableRow key={index} data-testid={`entry-row-${index}`}>
                  <TableCell>
                    <Input
                      value={entry.account_code}
                      onChange={(e) => updateEntry(index, 'account_code', e.target.value)}
                      className="h-8 font-mono text-sm"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={entry.account_name}
                      onChange={(e) => updateEntry(index, 'account_name', e.target.value)}
                      className="h-8"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      step="0.01"
                      value={entry.amount}
                      onChange={(e) => updateEntry(index, 'amount', parseFloat(e.target.value) || 0)}
                      className="h-8 tabular-nums text-right"
                    />
                  </TableCell>
                  <TableCell>
                    {eff.fromMapping ? (
                      <div className="flex items-center gap-1.5">
                        <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 shrink-0" title="Da Configura Conti"></span>
                        <span className="text-sm font-medium">{categoryLabel(eff.category)}</span>
                      </div>
                    ) : (
                      <Select 
                        value={entry.category || 'uncategorized'} 
                        onValueChange={(v) => updateEntry(index, 'category', v)}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="uncategorized">-- Non classificato --</SelectItem>
                          {categories.map((cat) => (
                            <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </TableCell>
                  <TableCell>
                    {eff.category === 'fixed_cost' && (
                      eff.fromMapping && eff.subcategory ? (
                        <span className="text-sm">{subcategories.find(s => s.value === eff.subcategory)?.label || eff.subcategory}</span>
                      ) : (
                        <Select 
                          value={entry.subcategory || ''} 
                          onValueChange={(v) => updateEntry(index, 'subcategory', v)}
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue placeholder="Seleziona..." />
                          </SelectTrigger>
                          <SelectContent>
                            {subcategories.map((sub) => (
                              <SelectItem key={sub.value} value={sub.value}>{sub.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )
                    )}
                  </TableCell>
                  <TableCell>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => deleteEntry(index)}
                      data-testid={`delete-entry-${index}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add Entry Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aggiungi Voce Contabile</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Codice Conto</Label>
                <Input
                  value={newEntry.account_code}
                  onChange={(e) => setNewEntry({ ...newEntry, account_code: e.target.value })}
                  placeholder="es. 57.09.01"
                  data-testid="new-entry-code"
                />
              </div>
              <div>
                <Label>Importo</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newEntry.amount}
                  onChange={(e) => setNewEntry({ ...newEntry, amount: parseFloat(e.target.value) || 0 })}
                  data-testid="new-entry-amount"
                />
              </div>
            </div>
            <div>
              <Label>Descrizione</Label>
              <Input
                value={newEntry.account_name}
                onChange={(e) => setNewEntry({ ...newEntry, account_name: e.target.value })}
                placeholder="Descrizione del conto"
                data-testid="new-entry-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Categoria</Label>
                <Select value={newEntry.category} onValueChange={(v) => setNewEntry({ ...newEntry, category: v })}>
                  <SelectTrigger data-testid="new-entry-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="uncategorized">-- Non classificato --</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {newEntry.category === 'fixed_cost' && (
                <div>
                  <Label>Sottocategoria</Label>
                  <Select value={newEntry.subcategory} onValueChange={(v) => setNewEntry({ ...newEntry, subcategory: v })}>
                    <SelectTrigger data-testid="new-entry-subcategory">
                      <SelectValue placeholder="Seleziona..." />
                    </SelectTrigger>
                    <SelectContent>
                      {subcategories.map((sub) => (
                        <SelectItem key={sub.value} value={sub.value}>{sub.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Annulla</Button>
            <Button onClick={addEntry} data-testid="confirm-add-entry">Aggiungi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Client Detail Page
const ClientDetail = () => {
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(2026);
  const [selectedMonth, setSelectedMonth] = useState(1);
  const [activeTab, setActiveTab] = useState("overview");
  const [trendData, setTrendData] = useState(null);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [generatingSimpleReport, setGeneratingSimpleReport] = useState(false);
  const [reportPdfUrl, setReportPdfUrl] = useState(null);
  const [showCompileDialog, setShowCompileDialog] = useState(false);
  const [compilations, setCompilations] = useState([]);
  const [compiling, setCompiling] = useState(false);
  const [compareYears, setCompareYears] = useState([]);
  const [compareMode, setCompareMode] = useState('like_for_like');
  const [presentMode, setPresentMode] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(() => {
    try { return parseInt(localStorage.getItem('cdg_zoom') || '100'); } catch { return 100; }
  });
  const navigate = useNavigate();
  const { setNav } = useClientNav();
  const { user } = useAuth();
  const isAdmin = (user?.role || "admin") === "admin";

  // Register/unregister sidebar navigation
  useEffect(() => {
    if (client) {
      setNav({ activeTab, setActiveTab, clientName: client.name, clientId: client.id, fiscalRegime: client.fiscal_regime, businessType: client.business_type, presentMode, setPresentMode });
    }
  }, [client, activeTab, setNav, presentMode]);

  // For client_viewer: ensure activeTab is a page they're allowed to see.
  useEffect(() => {
    if (!isAdmin && client && user) {
      const access = (user.accessible_clients || []).find((a) => a.client_id === client.id);
      const allowed = new Set(access?.allowed_pages || ["overview"]);
      if (!allowed.has(activeTab)) {
        const fallback = allowed.has("overview") ? "overview" : Array.from(allowed)[0];
        if (fallback) setActiveTab(fallback);
      }
    }
  }, [isAdmin, client, user, activeTab]);

  // Zoom persistence
  useEffect(() => {
    localStorage.setItem('cdg_zoom', String(zoomLevel));
  }, [zoomLevel]);

  useEffect(() => {
    return () => setNav(null);
  }, [setNav]);

  useEffect(() => {
    fetchData();
  }, [clientId]);

  useEffect(() => {
    if (client) {
      fetchComparison();
      fetchTrend();
    }
  }, [selectedYear, selectedMonth, client]);

  const fetchData = async () => {
    try {
      const [clientRes, periodsRes, mappingsRes] = await Promise.all([
        axios.get(`${API}/clients/${clientId}`),
        axios.get(`${API}/clients/${clientId}/periods`),
        axios.get(`${API}/clients/${clientId}/mappings`)
      ]);
      setClient(clientRes.data);
      setPeriods(periodsRes.data);
      setMappings(mappingsRes.data);

      // Set default period to most recent
      if (periodsRes.data.length > 0) {
        setSelectedYear(periodsRes.data[0].year);
        setSelectedMonth(periodsRes.data[0].month);
      }
    } catch (e) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const fetchComparison = async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}/comparison`, {
        params: { year: selectedYear, month: selectedMonth }
      });
      setComparison(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchTrend = async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}/annual-trend`, {
        params: { year: selectedYear }
      });
      setTrendData(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) return <div className="p-8 text-center">Caricamento...</div>;
  if (!client) return <div className="p-8 text-center">Cliente non trovato</div>;

  const currentPeriod = comparison?.current_period;
  const analysis = currentPeriod?.analysis || {};

  const generateReport = async () => {
    setGeneratingReport(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/clients/${clientId}/generate-report?year=${selectedYear}&month=${selectedMonth}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob', timeout: 120000 }
      );
      const reportId = response.headers['x-report-id'];
      if (reportId) {
        setReportPdfUrl(`${API}/reports/${reportId}`);
      }
      toast.success("Report PDF generato!");
    } catch (e) {
      toast.error("Errore generazione report: " + (e.response?.data?.detail || e.message));
    } finally {
      setGeneratingReport(false);
    }
  };

  const generateSimpleReport = async () => {
    setGeneratingSimpleReport(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/clients/${clientId}/simple-report?year=${selectedYear}&month=${selectedMonth}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob', timeout: 60000 }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Report_Semplice_${client?.name || 'cliente'}_${selectedMonth}_${selectedYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report semplice scaricato!");
    } catch (e) {
      toast.error("Errore: " + (e.response?.data?.detail || e.message));
    } finally {
      setGeneratingSimpleReport(false);
    }
  };

  const closeReportViewer = () => {
    setReportPdfUrl(null);
  };

  const downloadReport = () => {
    if (!reportPdfUrl) return;
    const link = document.createElement('a');
    link.href = reportPdfUrl;
    link.setAttribute('download', `Report_${client?.name || 'cliente'}_${selectedMonth}_${selectedYear}.pdf`);
    link.setAttribute('target', '_blank');
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // === Report Compilation ===
  const fetchCompilations = async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}/report-compilations`);
      setCompilations(res.data);
    } catch {}
  };

  const startCompilation = async (quarter) => {
    setCompiling(true);
    try {
      const body = { year: selectedYear };
      if (quarter === 'annual') {
        body.months = null;
      } else {
        body.quarter = quarter;
      }
      const res = await axios.post(`${API}/clients/${clientId}/compile-report`, body);
      toast.success(`Compilazione "${res.data.label}" avviata!`);
      pollCompilation(res.data.id);
      fetchCompilations();
    } catch (e) {
      toast.error("Errore: " + (e.response?.data?.detail || e.message));
      setCompiling(false);
    }
  };

  const pollCompilation = (compId) => {
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const res = await axios.get(`${API}/clients/${clientId}/report-compilations`);
        setCompilations(res.data);
        const found = res.data.find(c => c.id === compId);
        if (found && found.status === 'done') {
          clearInterval(interval);
          setCompiling(false);
          toast.success("Compilazione completata!");
        } else if (found && found.status === 'error') {
          clearInterval(interval);
          setCompiling(false);
          toast.error("Errore: " + (found.progress || 'sconosciuto'));
        } else if (attempts >= 120) {
          clearInterval(interval);
          setCompiling(false);
          toast.error("Timeout compilazione");
        }
      } catch {}
    }, 5000);
  };

  const downloadCompilation = async (compId) => {
    try {
      const response = await axios.get(
        `${API}/clients/${clientId}/report-compilations/${compId}/download`,
        { responseType: 'blob', timeout: 60000 }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Raccolta_${client?.name || 'cliente'}_${selectedYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Errore download: " + (e.response?.data?.detail || e.message));
    }
  };

  const deleteCompilation = async (compId) => {
    try {
      await axios.delete(`${API}/clients/${clientId}/report-compilations/${compId}`);
      fetchCompilations();
      toast.success("Compilazione eliminata");
    } catch {}
  };

  const toggleCompareYear = (year) => {
    setCompareYears(prev =>
      prev.includes(year) ? prev.filter(y => y !== year) : prev.length < 4 ? [...prev, year] : prev
    );
  };

  const startComparativeReport = async () => {
    if (compareYears.length < 2) return toast.error("Seleziona almeno 2 anni");
    setCompiling(true);
    try {
      const res = await axios.post(`${API}/clients/${clientId}/comparative-report`, {
        years: compareYears,
        mode: compareMode,
      });
      toast.success(`Report comparativo "${res.data.label}" in generazione!`);
      pollCompilation(res.data.id);
      fetchCompilations();
    } catch (e) {
      toast.error("Errore: " + (e.response?.data?.detail || e.message));
      setCompiling(false);
    }
  };

  return (
    <div className={isAdmin ? "p-8" : "p-4 md:p-8"} data-testid="client-detail">
      {/* Viewer-only branded top bar */}
      {!isAdmin && (
        <div className="-mx-4 md:mx-0 mb-4 px-4 py-3 bg-slate-900 text-white md:rounded-xl">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-blue-300 font-semibold leading-none">Dottori Commercialisti</p>
              <p className="text-base font-black leading-tight mt-0.5">SC Casali · Silvia</p>
            </div>
            <div className="flex items-center gap-1.5">
              <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                <SelectTrigger className="h-8 w-[88px] bg-slate-800 border-slate-700 text-white text-xs" data-testid="month-select-mobile">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                    <SelectItem key={m} value={m.toString()}>{getMonthName(m)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger className="h-8 w-[72px] bg-slate-800 border-slate-700 text-white text-xs" data-testid="year-select-mobile">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[...new Set([...periods.map(p => p.year), new Date().getFullYear()])].sort((a,b) => b-a).map((y) => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-2 pt-2 border-t border-slate-700 flex items-center gap-2 flex-wrap">
            <Badge variant="secondary" className="bg-slate-800 text-slate-200 hover:bg-slate-700 text-[10px] border-0">{client.sector}</Badge>
            <h1 className="text-lg font-extrabold tracking-tight">{client.name}</h1>
          </div>
        </div>
      )}

      {/* Print-only branded header */}
      <div className="print-brand-header" style={{display: 'none'}}>
        <div style={{borderBottom: '3px solid #1e3a8a', paddingBottom: '8px', marginBottom: '12px'}}>
          <div style={{fontSize: '18px', fontWeight: 'bold', color: '#1e3a8a', textAlign: 'center', letterSpacing: '2px'}}>
            SC DOTTORI COMMERCIALISTI
          </div>
          <div style={{fontSize: '11px', color: '#6b7280', textAlign: 'center', marginTop: '2px'}}>
            Controllo di Gestione
          </div>
        </div>
        <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#6b7280', marginBottom: '8px'}}>
          <span>{client?.name}</span>
          <span>Periodo: {['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'][selectedMonth - 1]} {selectedYear}</span>
        </div>
      </div>

      {/* Data Health Banner — verifica coerenza dati salvati vs bilanci PDF */}
      {isAdmin && <DataHealthBanner clientId={clientId} isAdmin={isAdmin} />}

      {/* Header */}
      <div className={`flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4 ${!isAdmin ? "hidden md:flex" : ""}`}>
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge>{client.sector}</Badge>
            {client.business_type && client.business_type !== 'srl' && (
              <Badge variant="outline" className="border-violet-300 text-violet-700">
                {client.business_type === 'snc_sas' ? 'Società di persone' : client.business_type === 'ditta_individuale' ? 'Ditta Individuale' : client.business_type === 'professionista' ? 'Professionista' : client.business_type.toUpperCase()}
              </Badge>
            )}
          </div>
          <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight">{client.name}</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap no-print">
          <div className="hidden md:flex gap-2">
            <Button variant="outline" size="sm" onClick={() => {
              const activePanel = document.querySelector('[role="tabpanel"][data-state="active"]');
              if (activePanel) activePanel.classList.add('print-active');
              document.body.classList.add('printing-section');
              const cleanup = () => {
                document.body.classList.remove('printing-section');
                if (activePanel) activePanel.classList.remove('print-active');
                window.removeEventListener('afterprint', cleanup);
              };
              window.addEventListener('afterprint', cleanup);
              setTimeout(() => window.print(), 100);
            }} data-testid="print-btn">
              <Printer className="h-4 w-4 mr-1" /> <span className="hidden lg:inline">Stampa Sezione</span>
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowBulkUpload(true)} data-testid="bulk-upload-btn" hidden={!isAdmin} style={!isAdmin ? { display: 'none' } : undefined}>
              <FolderOpen className="h-4 w-4 mr-1" /> <span className="hidden lg:inline">Carica Pregresso</span>
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setShowCompileDialog(true); fetchCompilations(); }} data-testid="compile-report-btn" hidden={!isAdmin} style={!isAdmin ? { display: 'none' } : undefined}>
              <FileSpreadsheet className="h-4 w-4 mr-1" /> <span className="hidden lg:inline">Raccolta Report</span>
            </Button>
            <Button
              size="sm"
              variant={generatingSimpleReport ? "secondary" : "outline"}
              onClick={generateSimpleReport}
              disabled={generatingSimpleReport}
              data-testid="simple-report-btn"
              hidden={!isAdmin}
              style={!isAdmin ? { display: 'none' } : undefined}
            >
              {generatingSimpleReport ? (
                <><span className="animate-spin mr-1">&#9696;</span><span className="hidden lg:inline">Generazione...</span></>
              ) : (
                <><Mail className="h-4 w-4 mr-1" /> <span className="hidden lg:inline">Report via Mail</span></>
              )}
            </Button>
            <Button
              size="sm"
              variant={generatingReport ? "secondary" : "default"}
              className={generatingReport ? "" : "bg-amber-600 hover:bg-amber-700"}
              onClick={generateReport}
              disabled={generatingReport}
              data-testid="generate-report-btn"
              hidden={!isAdmin}
              style={!isAdmin ? { display: 'none' } : undefined}
            >
              {generatingReport ? (
                <><span className="animate-spin mr-1">&#9696;</span><span className="hidden lg:inline">Generazione...</span></>
              ) : (
                <><FileText className="h-4 w-4 mr-1" /> <span className="hidden lg:inline">Genera Report PDF</span></>
              )}
            </Button>
          </div>
          {isAdmin && (
            <Button size="sm" onClick={() => navigate(`/clients/${clientId}/upload`)} data-testid="upload-btn">
              <UploadCloud className="h-4 w-4 mr-1" /> Carica Bilancio
            </Button>
          )}
          {/* Zoom & Presentation controls (admin only) */}
          {isAdmin && (
            <>
              <div className="flex items-center border rounded-lg overflow-hidden h-8" data-testid="zoom-controls">
                <button onClick={() => setZoomLevel(z => Math.max(80, z - 10))} className="px-2 h-full hover:bg-slate-100 text-sm font-bold" data-testid="zoom-out">A-</button>
                <span className="px-2 text-xs tabular-nums bg-slate-50 h-full flex items-center border-x">{zoomLevel}%</span>
                <button onClick={() => setZoomLevel(z => Math.min(160, z + 10))} className="px-2 h-full hover:bg-slate-100 text-sm font-bold" data-testid="zoom-in">A+</button>
              </div>
              <Button
                size="sm"
                variant={presentMode ? "default" : "outline"}
                onClick={() => setPresentMode(p => !p)}
                className={presentMode ? "bg-blue-600 hover:bg-blue-700" : ""}
                data-testid="present-mode-btn"
              >
                {presentMode ? <><Minimize2 className="h-4 w-4 mr-1" /> Esci</> : <><Maximize2 className="h-4 w-4 mr-1" /> Presenta</>}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Bulk Upload Dialog */}
      <BulkUploadDialog
        clientId={clientId}
        clientName={client?.name}
        open={showBulkUpload}
        onOpenChange={setShowBulkUpload}
        onComplete={() => fetchData()}
      />

      {/* Report Compilation Dialog */}
      <Dialog open={showCompileDialog} onOpenChange={setShowCompileDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Report e Raccolte - {selectedYear}</DialogTitle>
            <DialogDescription>
              Genera raccolte di report mensili o confronti annuali con analisi AI.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {[
                { q: 1, label: 'Q1', sub: 'Gen - Mar' },
                { q: 2, label: 'Q2', sub: 'Apr - Giu' },
                { q: 3, label: 'Q3', sub: 'Lug - Set' },
                { q: 4, label: 'Q4', sub: 'Ott - Dic' },
              ].map(({ q, label, sub }) => (
                <Button key={q} variant="outline" className="h-auto py-3 flex flex-col" disabled={compiling}
                  onClick={() => startCompilation(q)} data-testid={`compile-q${q}-btn`}>
                  <span className="font-bold">{label}</span>
                  <span className="text-[10px] text-muted-foreground">{sub} {selectedYear}</span>
                </Button>
              ))}
            </div>
            <Button className="w-full" disabled={compiling} onClick={() => startCompilation('annual')} data-testid="compile-annual-btn">
              {compiling ? <><span className="animate-spin mr-2">&#9696;</span> Generazione...</> : `Anno Completo ${selectedYear}`}
            </Button>

            {/* Comparative Report Section */}
            <div className="border-t pt-3 mt-2">
              <p className="text-xs font-semibold text-muted-foreground mb-2">Report Comparativo Annuale (AI)</p>
              <p className="text-[10px] text-muted-foreground mb-2">Seleziona 2-4 anni per generare un confronto con analisi trend, grafici e commento narrativo AI</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {[selectedYear - 3, selectedYear - 2, selectedYear - 1, selectedYear].filter(y => y > 2018).map(yr => (
                  <Button key={yr} size="sm" variant={compareYears.includes(yr) ? "default" : "outline"}
                    className="h-8 text-xs" onClick={() => toggleCompareYear(yr)} data-testid={`compare-year-${yr}`}>
                    {yr}
                  </Button>
                ))}
              </div>
              {compareYears.length >= 2 && (
                <div className="flex gap-2 mb-3">
                  <Button size="sm" variant={compareMode === 'like_for_like' ? 'default' : 'outline'}
                    className="flex-1 h-auto py-2 text-[10px] flex flex-col" onClick={() => setCompareMode('like_for_like')}
                    data-testid="mode-like-for-like">
                    <span className="font-bold text-xs">Omogeneo</span>
                    <span className="opacity-70">Solo mesi in comune</span>
                  </Button>
                  <Button size="sm" variant={compareMode === 'projection' ? 'default' : 'outline'}
                    className="flex-1 h-auto py-2 text-[10px] flex flex-col" onClick={() => setCompareMode('projection')}
                    data-testid="mode-projection">
                    <span className="font-bold text-xs">Proiezione 12 mesi</span>
                    <span className="opacity-70">Stima anno intero</span>
                  </Button>
                </div>
              )}
              <Button className="w-full" size="sm" disabled={compiling || compareYears.length < 2}
                onClick={startComparativeReport} data-testid="start-comparative-btn">
                {compiling ? <><span className="animate-spin mr-2">&#9696;</span> Generazione...</> :
                  compareYears.length < 2 ? 'Seleziona almeno 2 anni' :
                  `Genera Confronto ${[...compareYears].sort().join(' - ')}`}
              </Button>
            </div>

            {compilations.length > 0 && (
              <div className="border-t pt-3 mt-3">
                <p className="text-xs font-semibold text-muted-foreground mb-2">Raccolte generate</p>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {compilations.map(c => (
                    <div key={c.id} className="flex items-center justify-between p-2 rounded border text-xs">
                      <div>
                        <p className="font-medium">{c.label}</p>
                        <p className="text-muted-foreground">
                          {c.status === 'done' ? c.progress : c.status === 'pending' ? c.progress || 'In corso...' : 'Errore'}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        {c.status === 'done' && (
                          <Button size="sm" variant="outline" className="h-7 text-[10px]"
                            onClick={() => downloadCompilation(c.id)} data-testid={`download-comp-${c.id}`}>
                            Scarica PDF
                          </Button>
                        )}
                        {c.status === 'pending' && (
                          <Badge variant="outline" className="text-[9px] animate-pulse">In corso</Badge>
                        )}
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-red-500"
                          onClick={() => deleteCompilation(c.id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF Report Viewer */}
      {reportPdfUrl && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center" data-testid="pdf-viewer-overlay">
          <div className="bg-white rounded-xl shadow-2xl w-[95vw] h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b bg-slate-50">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-amber-600" />
                <span className="font-semibold text-sm">Report PDF — {client?.name} — {getMonthName(selectedMonth)} {selectedYear}</span>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={downloadReport} data-testid="download-report-btn">
                  <Save className="h-4 w-4 mr-1" /> Scarica
                </Button>
                <Button size="sm" variant="ghost" onClick={closeReportViewer} data-testid="close-report-viewer-btn">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <iframe src={reportPdfUrl} className="flex-1 w-full" title="Report PDF" />
          </div>
        </div>
      )}

      {/* Period Selector */}
      <Card className={`mb-6 no-print ${!isAdmin ? "hidden md:block" : ""}`}>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Label>Periodo:</Label>
              <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                <SelectTrigger className="w-40" data-testid="month-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                    <SelectItem key={m} value={m.toString()}>{getMonthName(m)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger className="w-28" data-testid="year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[...new Set([...periods.map(p => p.year), new Date().getFullYear()])].sort((a,b) => b-a).map((y) => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isAdmin && (
              <Button variant="outline" size="sm" onClick={() => setActiveTab('account-config')}>
                <Settings className="h-4 w-4 mr-2" /> Configura Conti
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6" style={{
        transform: `scale(${zoomLevel / 100})`,
        transformOrigin: 'top left',
        width: `${10000 / zoomLevel}%`,
      }}>

        {/* Overview Tab */}
        <TabsContent value="overview" className="flex flex-col gap-6">
          {!currentPeriod ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Nessun dato per questo periodo</h3>
                <p className="text-muted-foreground mb-4">Carica un bilancio per iniziare l'analisi</p>
                <Button onClick={() => navigate(`/clients/${clientId}/upload`)}>
                  <UploadCloud className="h-4 w-4 mr-2" /> Carica Bilancio
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Period indicator (hidden on mobile for viewers — already shown in top bar) */}
              <div className={`bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 flex items-center gap-2 text-sm ${!isAdmin ? "hidden md:flex" : ""}`}>
                <CalendarDays className="h-4 w-4 text-blue-600" />
                <span className="text-blue-900">Dati di <strong>{getMonthName(selectedMonth)} {selectedYear}</strong></span>
                {isAdmin && (
                  <span className="text-blue-600 text-xs">(seleziona un altro periodo dal selettore sopra)</span>
                )}
              </div>

              {/* Celebration banner: shown when ALL key metrics are positive AND growing vs prev month AND prev year */}
              {(() => {
                const pm = comparison?.previous_month_period?.analysis;
                const py = comparison?.previous_year_period?.analysis;
                if (!pm || !py) return null;
                const metrics = ['revenue', 'margin_contribution', 'ebitda', 'net_result'];
                const allPositive = metrics.every(k => (analysis[k] || 0) > 0);
                const growingVsMonth = metrics.every(k => (analysis[k] || 0) > (pm[k] || 0));
                const growingVsYear = metrics.every(k => (analysis[k] || 0) > (py[k] || 0));
                if (!allPositive || !growingVsMonth || !growingVsYear) return null;
                return (
                  <CelebrationButton
                    subtitle={`Ricavi, MDC, EBITDA e Utile: tutti positivi e in crescita rispetto a ${getMonthName(selectedMonth === 1 ? 12 : selectedMonth - 1)} ${selectedMonth === 1 ? selectedYear - 1 : selectedYear} e a ${getMonthName(selectedMonth)} ${selectedYear - 1}.`}
                  />
                );
              })()}

              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <KPICard
                  title="Ricavi"
                  value={formatCurrency(analysis.revenue)}
                  trend={comparison?.previous_month_period != null}
                  trendValue={comparison?.previous_month_period ? (() => {
                    const prevRev = comparison.previous_month_period.analysis?.revenue || 0;
                    return prevRev > 0 ? ((analysis.revenue - prevRev) / prevRev * 100) : null;
                  })() : null}
                  trendLabel={comparison?.previous_month_period ? `vs mese prec. (${formatCurrency(comparison.previous_month_period.analysis?.revenue || 0)})` : null}
                  icon={BadgeEuro}
                />
                <KPICard
                  title="Margine di Contribuzione"
                  value={formatCurrency(analysis.margin_contribution)}
                  trend
                  trendValue={analysis.margin_contribution_pct}
                  icon={TrendingUp}
                />
                <KPICard
                  title="EBITDA"
                  value={formatCurrency(analysis.ebitda)}
                  trend
                  trendValue={analysis.ebitda_pct}
                  icon={BarChart3}
                />
                <KPICard
                  title="Utile ante Compenso Amm."
                  value={formatCurrency(analysis.net_result)}
                  trend
                  trendValue={analysis.net_result_pct}
                  icon={Target}
                />
              </div>

              {/* Owner Compensation Banner */}
              {comparison?.owner_compensation && (
                <Card className="border-violet-200 bg-violet-50/50">
                  <CardContent className="py-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Users className="h-5 w-5 text-violet-600" />
                      <span className="font-semibold text-violet-900">Compenso Soci/Titolare</span>
                      <Badge variant="outline" className="border-violet-300 text-violet-700 text-xs">
                        {formatCurrency(comparison.owner_compensation.monthly_amount)}/mese
                      </Badge>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-white rounded-lg p-3 border">
                        <div className="text-xs text-muted-foreground">Utile ante Compenso Amm.</div>
                        <div className="text-lg font-bold">{formatCurrency(analysis.net_result)}</div>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-violet-300">
                        <div className="text-xs text-violet-700 font-medium">Utile Netto (Civilistico)</div>
                        <div className={`text-lg font-bold ${comparison.owner_compensation.adjusted_net_result >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                          {formatCurrency(comparison.owner_compensation.adjusted_net_result)}
                          <span className="text-xs ml-1 font-normal">({comparison.owner_compensation.adjusted_net_result_pct?.toFixed(1)}%)</span>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-3 border">
                        <div className="text-xs text-muted-foreground">Costo Lavoro Effettivo (Dip. + Soci)</div>
                        <div className="text-lg font-bold text-violet-700">
                          {formatCurrency(comparison.owner_compensation.real_labor_cost)}
                          <span className="text-xs ml-1 font-normal">({comparison.owner_compensation.real_labor_cost_pct?.toFixed(1)}%)</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Gauges */}
              <Card className="bg-gradient-to-br from-slate-50 to-white order-first md:order-none">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-bold">Indicatori di Performance</CardTitle>
                  <CardDescription className="text-xs">Marginalita rispetto ai ricavi del periodo</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="flex flex-col items-center p-4 rounded-2xl bg-white border border-slate-200/80 shadow-sm hover:shadow-md transition-shadow">
                      <Tachimetro value={analysis.margin_contribution_pct || 0} label="MDC" thresholds={{ red: 20, yellow: 35, green: 50 }} />
                    </div>
                    <div className="flex flex-col items-center p-4 rounded-2xl bg-white border border-slate-200/80 shadow-sm hover:shadow-md transition-shadow">
                      <Tachimetro value={analysis.ebitda_pct || 0} label="EBITDA" thresholds={{ red: 5, yellow: 10, green: 18 }} />
                    </div>
                    <div className="flex flex-col items-center p-4 rounded-2xl bg-white border border-slate-200/80 shadow-sm hover:shadow-md transition-shadow">
                      <Tachimetro value={analysis.ebit_pct || 0} label="EBIT" thresholds={{ red: 2, yellow: 5, green: 12 }} />
                    </div>
                    <div className="flex flex-col items-center p-4 rounded-2xl bg-white border border-slate-200/80 shadow-sm hover:shadow-md transition-shadow">
                      <Tachimetro value={analysis.net_result_pct || 0} label="Utile" thresholds={{ red: 0, yellow: 2, green: 5 }} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* CE Sintetico + Waterfall Chart */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Mini CE Table */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-bold">Conto Economico Sintetico</CardTitle>
                    <CardDescription className="text-xs">{getMonthName(selectedMonth)} {selectedYear}</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-1 text-sm">
                      {[
                        { label: 'Ricavi', value: analysis.revenue, pct: 100, bold: true, color: 'text-slate-900' },
                        { label: '(-) Costi Variabili', value: -analysis.variable_costs, pct: analysis.variable_costs / (analysis.revenue || 1) * 100, indent: true, color: 'text-red-600' },
                        { label: '= Margine di Contribuzione', value: analysis.margin_contribution, pct: analysis.margin_contribution_pct, bold: true, color: 'text-emerald-700', bg: 'bg-emerald-50/60' },
                        { label: '(-) Costi Fissi', value: -analysis.fixed_costs, pct: analysis.fixed_costs_pct, indent: true, color: 'text-red-600' },
                        { label: '= EBITDA', value: analysis.ebitda, pct: analysis.ebitda_pct, bold: true, color: 'text-blue-700', bg: 'bg-blue-50/60' },
                        { label: '(-) Ammortamenti', value: -analysis.depreciation, pct: null, indent: true, color: 'text-slate-500' },
                        { label: '= EBIT', value: analysis.ebit, pct: analysis.ebit_pct, bold: true, color: 'text-indigo-700', bg: 'bg-indigo-50/60' },
                        { label: '(-) Oneri Finanziari', value: -analysis.financial_charges, pct: null, indent: true, color: 'text-slate-500' },
                        { label: '= Utile ante Compenso Amm.', value: analysis.net_result, pct: analysis.net_result_pct, bold: true, color: analysis.net_result >= 0 ? 'text-emerald-700' : 'text-red-700', bg: analysis.net_result >= 0 ? 'bg-emerald-50/60' : 'bg-red-50/60' },
                      ].map((row, i) => (
                        <div key={i} className={`flex items-center justify-between py-1.5 px-2 rounded ${row.bg || ''} ${row.indent ? 'pl-5' : ''}`}>
                          <span className={`${row.bold ? 'font-bold' : 'font-normal'} ${row.color || ''} text-xs`}>{row.label}</span>
                          <div className="flex items-center gap-3">
                            <span className={`tabular-nums text-xs ${row.bold ? 'font-bold' : ''} ${row.color || ''}`}>{formatCurrency(row.value)}</span>
                            {row.pct != null && <span className="tabular-nums text-[10px] text-muted-foreground w-10 text-right">{row.pct.toFixed(1)}%</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Cost Structure Chart */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-bold">Struttura dei Costi</CardTitle>
                    <CardDescription className="text-xs">Composizione per categoria</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={[
                        { name: "Ricavi", value: analysis.revenue || 0, fill: "#059669" },
                        { name: "C. Variabili", value: analysis.variable_costs || 0, fill: "#D97706" },
                        { name: "C. Fissi", value: analysis.fixed_costs || 0, fill: "#DC2626" },
                        { name: "Ammort.", value: analysis.depreciation || 0, fill: "#6366F1" },
                        { name: "Oneri Fin.", value: analysis.financial_charges || 0, fill: "#7C3AED" },
                      ]} barSize={36}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                        <YAxis tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                        <RechartsTooltip formatter={(v) => formatCurrency(v)} contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '12px' }} />
                        <Bar dataKey="value" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>


        {/* CE Annuale Tab */}
        <TabsContent value="ce-annuale" className="space-y-6">
          {isAdmin && <CumulativeBilanciSection clientId={clientId} year={selectedYear} />}
          <YearlyReportTable clientId={clientId} clientName={client?.name} />
        </TabsContent>

        {/* CE per Sede Tab */}
        <TabsContent value="ce-sede" className="space-y-6">
          <LocationAnalysisPage clientId={clientId} year={selectedYear} />
        </TabsContent>

        {/* Marketing ROI Tab */}
        <TabsContent value="marketing-roi" className="space-y-6">
          <MarketingROIPage clientId={clientId} year={selectedYear} />
        </TabsContent>

        {/* Service Invoices Tab */}
        <TabsContent value="service-invoices" className="space-y-6">
          <ServiceInvoicesPage clientId={clientId} />
        </TabsContent>

        {/* KPI Dashboard Tab */}
        <TabsContent value="kpi" className="space-y-6">
          <KPIDashboard clientId={clientId} year={selectedYear} month={selectedMonth} />
        </TabsContent>

        {/* Briefing Tab */}
        <TabsContent value="briefing" className="space-y-6">
          <BriefingPage clientId={clientId} year={selectedYear} month={selectedMonth} clientName={client?.name} />
        </TabsContent>

        {/* Commitments Tab */}
        <TabsContent value="commitments" className="space-y-6">
          <CommitmentsTab clientId={clientId} clientName={client?.name} />
        </TabsContent>

        {/* Annual Depreciation Tab */}
        <TabsContent value="depreciation-annual" className="space-y-6">
          <AnnualDepreciationSection clientId={clientId} year={selectedYear} onUpdated={() => {}} />
        </TabsContent>

        {/* Cash Flow Tab */}
        <TabsContent value="cashflow" className="space-y-6">
          <CashFlowPage clientId={clientId} parentYear={selectedYear} />
        </TabsContent>

        {/* Break Even Tab */}
        <TabsContent value="break-even" className="space-y-6">
          <BreakEvenPage clientId={clientId} clientName={client?.name} />
        </TabsContent>


        {/* Analysis Tab */}
        <TabsContent value="analysis" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Conto Economico Riclassificato</CardTitle>
              <CardDescription>{getMonthName(selectedMonth)} {selectedYear}</CardDescription>
            </CardHeader>
            <CardContent>
              <Table className="financial-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Voce</TableHead>
                    <TableHead className="text-right">Importo</TableHead>
                    <TableHead className="text-right">% Ricavi</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow className="total-row">
                    <TableCell className="font-bold">RICAVI</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCurrency(analysis.revenue)}</TableCell>
                    <TableCell className="text-right">100%</TableCell>
                    <TableCell className="text-center">
                      <Semaforo value={100} />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="indent-1">(-) Costi Variabili</TableCell>
                    <TableCell className="text-right tabular-nums text-red-600">{formatCurrency(-analysis.variable_costs)}</TableCell>
                    <TableCell className="text-right">{formatPercent(analysis.variable_costs / (analysis.revenue || 1) * 100)}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                  <TableRow className="subtotal-row bg-green-50">
                    <TableCell className="font-semibold text-green-700">= MARGINE DI CONTRIBUZIONE</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold text-green-700">{formatCurrency(analysis.margin_contribution)}</TableCell>
                    <TableCell className="text-right font-semibold">{formatPercent(analysis.margin_contribution_pct)}</TableCell>
                    <TableCell className="text-center">
                      <Semaforo value={analysis.margin_contribution_pct || 0} thresholds={{ red: 20, yellow: 35, green: 50 }} />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="indent-1">(-) Costi Fissi</TableCell>
                    <TableCell className="text-right tabular-nums text-red-600">{formatCurrency(-analysis.fixed_costs)}</TableCell>
                    <TableCell className="text-right">{formatPercent(analysis.fixed_costs_pct)}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                  <TableRow className="subtotal-row bg-blue-50">
                    <TableCell className="font-semibold text-blue-700">= EBITDA (MOL)</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold text-blue-700">{formatCurrency(analysis.ebitda)}</TableCell>
                    <TableCell className="text-right font-semibold">{formatPercent(analysis.ebitda_pct)}</TableCell>
                    <TableCell className="text-center">
                      <Semaforo value={analysis.ebitda_pct || 0} thresholds={{ red: 5, yellow: 10, green: 18 }} />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="indent-1">(-) Ammortamenti</TableCell>
                    <TableCell className="text-right tabular-nums text-red-600">{formatCurrency(-analysis.depreciation)}</TableCell>
                    <TableCell className="text-right">{formatPercent(analysis.depreciation_pct)}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                  <TableRow className="subtotal-row bg-indigo-50">
                    <TableCell className="font-semibold text-indigo-700">= EBIT (Risultato Operativo)</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold text-indigo-700">{formatCurrency(analysis.ebit)}</TableCell>
                    <TableCell className="text-right font-semibold">{formatPercent(analysis.ebit_pct)}</TableCell>
                    <TableCell className="text-center">
                      <Semaforo value={analysis.ebit_pct || 0} thresholds={{ red: 2, yellow: 5, green: 12 }} />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="indent-1">(-) Oneri Finanziari</TableCell>
                    <TableCell className="text-right tabular-nums text-red-600">{formatCurrency(-analysis.financial_charges)}</TableCell>
                    <TableCell className="text-right">{formatPercent(analysis.financial_charges_pct)}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                  <TableRow className="subtotal-row">
                    <TableCell className="font-semibold">= Risultato Prima delle Imposte</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{formatCurrency(analysis.result_before_tax)}</TableCell>
                    <TableCell className="text-right font-semibold">{formatPercent(analysis.result_before_tax_pct)}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="indent-1">(-) Imposte</TableCell>
                    <TableCell className="text-right tabular-nums text-red-600">{formatCurrency(-analysis.taxes)}</TableCell>
                    <TableCell className="text-right"></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                  <TableRow className="total-row !bg-slate-800 text-white">
                    <TableCell className="font-bold">= RISULTATO NETTO</TableCell>
                    <TableCell className="text-right tabular-nums font-bold">{formatCurrency(analysis.net_result)}</TableCell>
                    <TableCell className="text-right font-bold">{formatPercent(analysis.net_result_pct)}</TableCell>
                    <TableCell className="text-center">
                      <Semaforo value={analysis.net_result_pct || 0} thresholds={{ red: 0, yellow: 2, green: 5 }} />
                    </TableCell>
                  </TableRow>
                  {(analysis.admin_compensation > 0 || analysis.brand_royalties > 0) && (<>
                  {analysis.admin_compensation > 0 && (
                  <TableRow>
                    <TableCell className="indent-1 text-violet-700">(-) Compenso Amministratore</TableCell>
                    <TableCell className="text-right tabular-nums text-violet-700">{formatCurrency(-analysis.admin_compensation)}</TableCell>
                    <TableCell className="text-right">{formatPercent(analysis.admin_compensation / (analysis.revenue || 1) * 100)}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                  )}
                  {analysis.brand_royalties > 0 && (
                  <TableRow>
                    <TableCell className="indent-1 text-violet-700">(-) Royalties sul Marchio</TableCell>
                    <TableCell className="text-right tabular-nums text-violet-700">{formatCurrency(-analysis.brand_royalties)}</TableCell>
                    <TableCell className="text-right">{formatPercent(analysis.brand_royalties / (analysis.revenue || 1) * 100)}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                  )}
                  <TableRow className="total-row !bg-violet-900 text-white">
                    <TableCell className="font-bold">= RISULTATO EFFETTIVO</TableCell>
                    <TableCell className="text-right tabular-nums font-bold">{formatCurrency(analysis.effective_result)}</TableCell>
                    <TableCell className="text-right font-bold">{formatPercent(analysis.effective_result_pct)}</TableCell>
                    <TableCell className="text-center">
                      <Semaforo value={analysis.effective_result_pct || 0} thresholds={{ red: 0, yellow: 2, green: 5 }} />
                    </TableCell>
                  </TableRow>
                  </>)}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trend Tab */}
        <TabsContent value="trend" className="space-y-6">
          {trendData ? (
            (() => {
              const hasPrev = trendData.previous_monthly?.some(m => m.has_data);
              const prevYear = trendData.previous_year;
              const curYear = selectedYear;

              // Merge current + previous year data per month for charts
              const mergedMonths = trendData.monthly_data.map((m, i) => {
                const pm = trendData.previous_monthly?.[i] || {};
                return {
                  month_name: m.month_name,
                  ...m,
                  // Previous year values with _prev suffix
                  revenue_prev: pm.has_data ? pm.revenue : null,
                  margin_contribution_prev: pm.has_data ? pm.margin_contribution : null,
                  ebitda_prev: pm.has_data ? pm.ebitda : null,
                  net_result_prev: pm.has_data ? pm.net_result : null,
                  margin_contribution_pct_prev: pm.has_data ? pm.margin_contribution_pct : null,
                  ebitda_pct_prev: pm.has_data ? pm.ebitda_pct : null,
                  net_result_pct_prev: pm.has_data ? pm.net_result_pct : null,
                  ytd_revenue_prev: pm.has_data ? pm.ytd_revenue : null,
                  ytd_mdc_prev: pm.has_data ? pm.ytd_mdc : null,
                  ytd_ebitda_prev: pm.has_data ? pm.ytd_ebitda : null,
                  ytd_net_prev: pm.has_data ? pm.ytd_net : null,
                  has_prev_data: pm.has_data,
                };
              });

              return (
              <>
              {/* Color legend for dual-year charts */}
              {hasPrev && (
                <div className="flex items-center gap-6 p-3 bg-slate-50 rounded-lg border text-sm">
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-4 h-4 rounded bg-emerald-500" />
                    <span className="inline-block w-4 h-4 rounded bg-blue-500" />
                    <span className="inline-block w-4 h-4 rounded bg-violet-500" />
                    <span className="font-semibold">{curYear}</span>
                    <span className="text-muted-foreground">colori vivaci</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-4 h-4 rounded bg-slate-300" />
                    <span className="inline-block w-4 h-4 rounded bg-slate-400" />
                    <span className="font-semibold text-slate-500">{prevYear}</span>
                    <span className="text-muted-foreground">toni grigi</span>
                  </div>
                </div>
              )}

              {/* Monthly Revenue & Margins Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Andamento Ricavi ed EBITDA {hasPrev ? `${curYear} vs ${prevYear}` : curYear}</CardTitle>
                  <CardDescription>
                    {trendData.periods_count} mesi caricati {curYear}
                    {hasPrev && ` · Barre grigie = ${prevYear}`}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={mergedMonths.filter(m => m.has_data || m.has_prev_data)} barGap={2} barCategoryGap="20%">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month_name" />
                      <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                      <RechartsTooltip formatter={(v, name) => [formatCurrency(v), name]} />
                      <Legend />
                      {hasPrev && <Bar dataKey="revenue_prev" name={`Ricavi ${prevYear}`} fill="#CBD5E1" radius={[2, 2, 0, 0]} />}
                      <Bar dataKey="revenue" name={`Ricavi ${curYear}`} fill="#10B981" radius={[4, 4, 0, 0]} />
                      {hasPrev && <Bar dataKey="ebitda_prev" name={`EBITDA ${prevYear}`} fill="#94A3B8" radius={[2, 2, 0, 0]} />}
                      <Bar dataKey="ebitda" name={`EBITDA ${curYear}`} fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Margin Percentages Trend */}
              <Card>
                <CardHeader>
                  <CardTitle>Andamento Margini % {hasPrev ? `${curYear} vs ${prevYear}` : curYear}</CardTitle>
                  <CardDescription>
                    Incidenza percentuale sui ricavi
                    {hasPrev && ` · Linee grigie = ${prevYear}`}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Custom legend - big and clear */}
                  <div className="flex gap-6 mb-4 text-sm font-semibold">
                    <span className="flex items-center gap-2"><span className="w-5 h-3 rounded bg-emerald-500 inline-block" /> MDC % (dopo fornitori)</span>
                    <span className="flex items-center gap-2"><span className="w-5 h-3 rounded bg-orange-500 inline-block" /> EBITDA % (cassa prodotta)</span>
                    <span className="flex items-center gap-2"><span className="w-5 h-3 rounded bg-sky-500 inline-block" /> Utile % (netto finale)</span>
                  </div>
                  <ResponsiveContainer width="100%" height={340}>
                    <AreaChart data={mergedMonths.filter(m => m.has_data || m.has_prev_data)}>
                      <defs>
                        <linearGradient id="gradMDC" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10B981" stopOpacity={0.2}/><stop offset="95%" stopColor="#10B981" stopOpacity={0.02}/></linearGradient>
                        <linearGradient id="gradEBITDA" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#F97316" stopOpacity={0.2}/><stop offset="95%" stopColor="#F97316" stopOpacity={0.02}/></linearGradient>
                        <linearGradient id="gradUtile" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.15}/><stop offset="95%" stopColor="#0EA5E9" stopOpacity={0.02}/></linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="month_name" tick={{ fontSize: 12 }} />
                      <YAxis tickFormatter={(v) => `${v}%`} domain={[0, 'auto']} tick={{ fontSize: 11 }} />
                      <RechartsTooltip
                        formatter={(v, name) => v != null ? [`${v.toFixed(1)}%`, name] : ['-', name]}
                        contentStyle={{ fontSize: 13, borderRadius: 8 }}
                      />
                      {/* Previous year - thin gray dashed, behind everything */}
                      {hasPrev && <Area type="monotone" dataKey="margin_contribution_pct_prev" name={`MDC % ${prevYear}`} stroke="#94A3B8" strokeWidth={1.5} strokeDasharray="5 3" fill="none" dot={{ r: 2, fill: '#94A3B8' }} connectNulls />}
                      {hasPrev && <Area type="monotone" dataKey="ebitda_pct_prev" name={`EBITDA % ${prevYear}`} stroke="#A1A1AA" strokeWidth={1.5} strokeDasharray="5 3" fill="none" dot={{ r: 2, fill: '#A1A1AA' }} connectNulls />}
                      {hasPrev && <Area type="monotone" dataKey="net_result_pct_prev" name={`Utile % ${prevYear}`} stroke="#D4D4D8" strokeWidth={1.5} strokeDasharray="5 3" fill="none" dot={{ r: 2, fill: '#D4D4D8' }} connectNulls />}
                      {/* Current year - thick, colored areas, on top */}
                      <Area type="monotone" dataKey="margin_contribution_pct" name={`MDC % ${curYear}`} stroke="#10B981" strokeWidth={3} fill="url(#gradMDC)"
                        dot={{ r: 6, fill: '#10B981', stroke: '#fff', strokeWidth: 2 }}
                        label={{ position: 'top', fontSize: 12, fontWeight: 700, fill: '#059669', formatter: (v) => `${v}%` }}
                        connectNulls />
                      <Area type="monotone" dataKey="ebitda_pct" name={`EBITDA % ${curYear}`} stroke="#F97316" strokeWidth={3} fill="url(#gradEBITDA)"
                        dot={{ r: 6, fill: '#F97316', stroke: '#fff', strokeWidth: 2 }}
                        label={{ position: 'top', fontSize: 12, fontWeight: 700, fill: '#EA580C', formatter: (v) => `${v}%` }}
                        connectNulls />
                      <Area type="monotone" dataKey="net_result_pct" name={`Utile % ${curYear}`} stroke="#0EA5E9" strokeWidth={3} fill="url(#gradUtile)"
                        dot={{ r: 6, fill: '#0EA5E9', stroke: '#fff', strokeWidth: 2 }}
                        label={{ position: 'bottom', fontSize: 12, fontWeight: 700, fill: '#0284C7', formatter: (v) => `${v}%` }}
                        connectNulls />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* YTD Cumulative */}
              <Card>
                <CardHeader>
                  <CardTitle>Progressivo Cumulato (YTD) {hasPrev ? `${curYear} vs ${prevYear}` : curYear}</CardTitle>
                  <CardDescription>
                    Totali progressivi da inizio anno
                    {hasPrev && ` · Linee grigie = ${prevYear}`}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Custom legend */}
                  <div className="flex gap-6 mb-4 text-sm font-semibold">
                    <span className="flex items-center gap-2"><span className="w-5 h-3 rounded bg-emerald-500 inline-block" /> Ricavi cumulati</span>
                    <span className="flex items-center gap-2"><span className="w-5 h-3 rounded bg-orange-500 inline-block" /> EBITDA cumulato</span>
                    <span className="flex items-center gap-2"><span className="w-5 h-3 rounded bg-sky-500 inline-block" /> Utile cumulato</span>
                  </div>
                  <ResponsiveContainer width="100%" height={340}>
                    <AreaChart data={mergedMonths.filter(m => m.has_data || m.has_prev_data)}>
                      <defs>
                        <linearGradient id="gradYTDRev" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10B981" stopOpacity={0.15}/><stop offset="95%" stopColor="#10B981" stopOpacity={0.02}/></linearGradient>
                        <linearGradient id="gradYTDEbitda" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#F97316" stopOpacity={0.15}/><stop offset="95%" stopColor="#F97316" stopOpacity={0.02}/></linearGradient>
                        <linearGradient id="gradYTDNet" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.12}/><stop offset="95%" stopColor="#0EA5E9" stopOpacity={0.02}/></linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="month_name" tick={{ fontSize: 12 }} />
                      <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                      <RechartsTooltip
                        formatter={(v, name) => v != null ? [formatCurrency(v), name] : ['-', name]}
                        contentStyle={{ fontSize: 13, borderRadius: 8 }}
                      />
                      {/* Previous year - thin gray dashed */}
                      {hasPrev && <Area type="monotone" dataKey="ytd_revenue_prev" name={`Ricavi YTD ${prevYear}`} stroke="#CBD5E1" strokeWidth={1.5} strokeDasharray="5 3" fill="none" dot={{ r: 2, fill: '#CBD5E1' }} connectNulls />}
                      {hasPrev && <Area type="monotone" dataKey="ytd_ebitda_prev" name={`EBITDA YTD ${prevYear}`} stroke="#A1A1AA" strokeWidth={1.5} strokeDasharray="5 3" fill="none" dot={{ r: 2, fill: '#A1A1AA' }} connectNulls />}
                      {hasPrev && <Area type="monotone" dataKey="ytd_net_prev" name={`Utile YTD ${prevYear}`} stroke="#D4D4D8" strokeWidth={1.5} strokeDasharray="5 3" fill="none" dot={{ r: 2, fill: '#D4D4D8' }} connectNulls />}
                      {/* Current year - vivid areas with labels */}
                      <Area type="monotone" dataKey="ytd_revenue" name={`Ricavi YTD ${curYear}`} stroke="#10B981" strokeWidth={3} fill="url(#gradYTDRev)"
                        dot={{ r: 6, fill: '#10B981', stroke: '#fff', strokeWidth: 2 }}
                        label={{ position: 'top', fontSize: 11, fontWeight: 700, fill: '#059669', formatter: (v) => `${(v/1000).toFixed(0)}k` }}
                        connectNulls />
                      <Area type="monotone" dataKey="ytd_ebitda" name={`EBITDA YTD ${curYear}`} stroke="#F97316" strokeWidth={3} fill="url(#gradYTDEbitda)"
                        dot={{ r: 6, fill: '#F97316', stroke: '#fff', strokeWidth: 2 }}
                        label={{ position: 'top', fontSize: 11, fontWeight: 700, fill: '#EA580C', formatter: (v) => `${(v/1000).toFixed(0)}k` }}
                        connectNulls />
                      <Area type="monotone" dataKey="ytd_net" name={`Utile YTD ${curYear}`} stroke="#0EA5E9" strokeWidth={3} fill="url(#gradYTDNet)"
                        dot={{ r: 6, fill: '#0EA5E9', stroke: '#fff', strokeWidth: 2 }}
                        label={{ position: 'bottom', fontSize: 11, fontWeight: 700, fill: '#0284C7', formatter: (v) => `${(v/1000).toFixed(0)}k` }}
                        connectNulls />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Monthly Data Table with both years */}
              <Card>
                <CardHeader>
                  <CardTitle>Riepilogo Mensile {hasPrev ? `${curYear} vs ${prevYear}` : curYear}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="sticky left-0 bg-white z-10" rowSpan={2}>Mese</TableHead>
                          <TableHead className="text-center bg-emerald-50 border-b-0" colSpan={hasPrev ? 2 : 1}>Ricavi</TableHead>
                          <TableHead className="text-center bg-blue-50 border-b-0" colSpan={hasPrev ? 2 : 1}>MDC %</TableHead>
                          <TableHead className="text-center bg-violet-50 border-b-0" colSpan={hasPrev ? 2 : 1}>EBITDA</TableHead>
                          <TableHead className="text-center bg-amber-50 border-b-0" colSpan={hasPrev ? 2 : 1}>Utile</TableHead>
                          {hasPrev && <TableHead className="text-center bg-slate-50 border-b-0">Var. Ricavi</TableHead>}
                        </TableRow>
                        <TableRow>
                          <TableHead className="text-right bg-emerald-50 text-xs">{curYear}</TableHead>
                          {hasPrev && <TableHead className="text-right bg-emerald-50/50 text-xs text-slate-500">{prevYear}</TableHead>}
                          <TableHead className="text-right bg-blue-50 text-xs">{curYear}</TableHead>
                          {hasPrev && <TableHead className="text-right bg-blue-50/50 text-xs text-slate-500">{prevYear}</TableHead>}
                          <TableHead className="text-right bg-violet-50 text-xs">{curYear}</TableHead>
                          {hasPrev && <TableHead className="text-right bg-violet-50/50 text-xs text-slate-500">{prevYear}</TableHead>}
                          <TableHead className="text-right bg-amber-50 text-xs">{curYear}</TableHead>
                          {hasPrev && <TableHead className="text-right bg-amber-50/50 text-xs text-slate-500">{prevYear}</TableHead>}
                          {hasPrev && <TableHead className="text-right bg-slate-50 text-xs">YoY %</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {mergedMonths.map((m) => {
                          const yoyPct = m.has_data && m.has_prev_data && m.revenue_prev > 0
                            ? ((m.revenue - m.revenue_prev) / m.revenue_prev * 100)
                            : null;
                          return (
                            <TableRow key={m.month} className={!m.has_data && !m.has_prev_data ? 'opacity-30' : ''}>
                              <TableCell className="sticky left-0 bg-white z-10 font-medium">{m.month_name}</TableCell>
                              <TableCell className="text-right tabular-nums">{m.has_data ? formatCurrency(m.revenue) : '-'}</TableCell>
                              {hasPrev && <TableCell className="text-right tabular-nums text-slate-400">{m.has_prev_data ? formatCurrency(m.revenue_prev) : '-'}</TableCell>}
                              <TableCell className="text-right tabular-nums">{m.has_data ? `${m.margin_contribution_pct}%` : '-'}</TableCell>
                              {hasPrev && <TableCell className="text-right tabular-nums text-slate-400">{m.has_prev_data ? `${m.margin_contribution_pct_prev}%` : '-'}</TableCell>}
                              <TableCell className="text-right tabular-nums">{m.has_data ? formatCurrency(m.ebitda) : '-'}</TableCell>
                              {hasPrev && <TableCell className="text-right tabular-nums text-slate-400">{m.has_prev_data ? formatCurrency(m.ebitda_prev) : '-'}</TableCell>}
                              <TableCell className="text-right tabular-nums">{m.has_data ? formatCurrency(m.net_result) : '-'}</TableCell>
                              {hasPrev && <TableCell className="text-right tabular-nums text-slate-400">{m.has_prev_data ? formatCurrency(m.net_result_prev) : '-'}</TableCell>}
                              {hasPrev && <TableCell className={`text-right tabular-nums font-semibold ${yoyPct != null ? (yoyPct >= 0 ? 'text-emerald-600' : 'text-red-500') : ''}`}>
                                {yoyPct != null ? `${yoyPct >= 0 ? '+' : ''}${yoyPct.toFixed(1)}%` : '-'}
                              </TableCell>}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
              </>
              );
            })()
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <BarChart3 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Carica più mesi per vedere il trend</h3>
                <p className="text-muted-foreground">I grafici appariranno man mano che carichi i bilanci mensili</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Costs Detail Tab */}
        <TabsContent value="costs" className="space-y-6">
          <CostsDetailSection clientId={clientId} year={selectedYear} />
        </TabsContent>

        {/* Editor Tab */}
        {/* Product Margins Tab */}
        <TabsContent value="product-margins" className="space-y-6">
          <ProductMarginPage clientId={clientId} year={selectedYear} clientName={client?.name} />
        </TabsContent>

        {/* Salon Goals Tab */}
        <TabsContent value="salon-goals" className="space-y-6">
          <SalonGoalsPage clientId={clientId} clientName={client?.name} />
        </TabsContent>

        {/* Tax Planning Tab */}
        <TabsContent value="tax-planning" className="space-y-6">
          <TaxPlanningPage clientId={clientId} year={selectedYear} />
        </TabsContent>

        <TabsContent value="forfettario" className="space-y-6">
          <ForfettarioPage clientId={clientId} clientName={client?.name} consultantName={user?.name} />
        </TabsContent>

        {/* Whale Curve Tab */}
        <TabsContent value="whale-curve" className="space-y-6">
          <WhaleCurvePage clientId={clientId} clientName={client?.name} selectedYear={selectedYear} />
        </TabsContent>

        <TabsContent value="job-margins" className="space-y-6">
          <JobProfitabilityPage clientId={clientId} />
        </TabsContent>

        <TabsContent value="market-research" className="space-y-6">
          <MarketResearchPage clientId={clientId} clientName={client?.name} />
        </TabsContent>

        <TabsContent value="potential-demand" className="space-y-6">
          <PotentialDemandPage clientId={clientId} client={client} />
        </TabsContent>

        <TabsContent value="clinic-kpi" className="space-y-6">
          <ClinicKpiDashboard clientId={clientId} />
        </TabsContent>

        <TabsContent value="data-verification" className="space-y-6">
          <DataVerificationPage clientId={clientId} year={selectedYear} month={selectedMonth} />
        </TabsContent>

        <TabsContent value="balance-sheet" className="space-y-6">
          <BalanceSheetProgressive clientId={clientId} periods={periods} />
        </TabsContent>

        <TabsContent value="rendiconto" className="space-y-6">
          <RendicontoFinanziario clientId={clientId} periods={periods} />
        </TabsContent>

        <TabsContent value="bank-rating" className="space-y-6">
          <BankRatingPage clientId={clientId} year={selectedYear} />
        </TabsContent>

        <TabsContent value="account-config" className="space-y-6">
          <AccountConfiguration clientId={clientId} clientName={client?.name} chartType={client?.chart_of_accounts} />
        </TabsContent>

        <TabsContent value="editor" className="space-y-6">
          <EntriesEditor
            clientId={clientId}
            periodId={currentPeriod?.id}
            entries={currentPeriod?.entries || []}
            mappings={mappings}
            onUpdate={fetchData}
          />
        </TabsContent>

        {/* Comparison Tab */}
        <TabsContent value="comparison" className="space-y-6">
          {/* Revenue Line Chart: Current Year vs Previous Year */}
          {trendData && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-blue-600" />
                  Andamento Fatturato {selectedYear} vs {selectedYear - 1}
                </CardTitle>
                <CardDescription>Confronto stagionale mese per mese — identifica tendenze e previsioni</CardDescription>
              </CardHeader>
              <CardContent>
                {(() => {
                  const months = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];
                  const chartData = months.map((name, i) => {
                    const cur = trendData.monthly_data?.[i];
                    const prev = trendData.previous_monthly?.[i];
                    return {
                      month: name,
                      [selectedYear]: cur?.has_data ? cur.revenue : null,
                      [selectedYear - 1]: prev?.has_data ? prev.revenue : null,
                    };
                  });
                  const hasCurData = chartData.some(d => d[selectedYear] !== null);
                  const hasPrevData = chartData.some(d => d[selectedYear - 1] !== null);
                  if (!hasCurData && !hasPrevData) return <p className="text-muted-foreground text-center py-8">Nessun dato disponibile per il confronto annuale</p>;
                  return (
                    <ResponsiveContainer width="100%" height={350}>
                      <LineChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} />
                        <RechartsTooltip formatter={(v) => formatCurrency(v)} />
                        <Legend />
                        {hasCurData && (
                          <Line type="monotone" dataKey={selectedYear} name={`${selectedYear}`} stroke="#2563EB" strokeWidth={3} dot={{ r: 5, fill: '#2563EB' }} activeDot={{ r: 7 }} connectNulls={false} />
                        )}
                        {hasPrevData && (
                          <Line type="monotone" dataKey={selectedYear - 1} name={`${selectedYear - 1}`} stroke="#94A3B8" strokeWidth={2} strokeDasharray="8 4" dot={{ r: 4, fill: '#94A3B8' }} connectNulls={false} />
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Month Comparison */}
            <Card>
              <CardHeader>
                <CardTitle>Confronto Mensile</CardTitle>
                <CardDescription>
                  {getMonthName(selectedMonth)} {selectedYear} vs {getMonthName(selectedMonth)} {selectedYear - 1}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {comparison?.current_period && comparison?.previous_year_period ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Voce</TableHead>
                        <TableHead className="text-right">{selectedYear}</TableHead>
                        <TableHead className="text-right">{selectedYear - 1}</TableHead>
                        <TableHead className="text-right">Var %</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {[
                        { key: "revenue", label: "Ricavi" },
                        { key: "margin_contribution", label: "MDC" },
                        { key: "ebitda", label: "EBITDA" },
                        { key: "ebit", label: "EBIT" },
                        { key: "net_result", label: "Utile ante Compenso Amm." },
                      ].map((item) => {
                        const curr = comparison.current_period.analysis[item.key] || 0;
                        const prev = comparison.previous_year_period.analysis[item.key] || 0;
                        const varPct = prev !== 0 ? ((curr - prev) / Math.abs(prev)) * 100 : 0;
                        return (
                          <TableRow key={item.key}>
                            <TableCell>{item.label}</TableCell>
                            <TableCell className="text-right tabular-nums">{formatCurrency(curr)}</TableCell>
                            <TableCell className="text-right tabular-nums">{formatCurrency(prev)}</TableCell>
                            <TableCell className={`text-right tabular-nums ${varPct >= 0 ? "text-green-600" : "text-red-600"}`}>
                              {varPct >= 0 ? "+" : ""}{varPct.toFixed(1)}%
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-muted-foreground text-center py-8">
                    Dati non disponibili per il confronto
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Benchmark Comparison */}
            <Card>
              <CardHeader>
                <CardTitle>Confronto Benchmark Settore</CardTitle>
                <CardDescription>Struttura costi vs Media ISTAT e Best operativo (% sul fatturato)</CardDescription>
              </CardHeader>
              <CardContent>
                {comparison?.benchmark && Object.keys(comparison.benchmark).length > 0 && analysis.revenue > 0 ? (
                  <div className="space-y-6">
                    {[
                      { label: "Acquisti Beni e Servizi", valueKey: "variable_costs", avgKey: "acquisti_pct_avg", bestKey: "acquisti_pct_best", lowerIsBetter: true },
                      { label: "Costo del Personale", valueKey: "fixed_costs_personnel", avgKey: "personale_pct_avg", bestKey: "personale_pct_best", lowerIsBetter: true },
                      { label: "EBITDA / MOL", valueKey: "ebitda_pct", avgKey: "ebitda_pct_avg", bestKey: "ebitda_pct_best", lowerIsBetter: false },
                    ].map((item) => {
                      let value;
                      if (item.valueKey === "variable_costs") {
                        value = analysis.revenue > 0 ? (analysis.variable_costs / analysis.revenue * 100) : 0;
                      } else if (item.valueKey === "fixed_costs_personnel") {
                        const personnelItem = Array.isArray(analysis.fixed_costs_detail) 
                          ? analysis.fixed_costs_detail.find(d => d.key === 'personnel') 
                          : null;
                        const personnelCost = personnelItem?.total || 0;
                        // Include owner compensation if present
                        const ownerComp = comparison?.owner_compensation?.monthly_amount || 0;
                        const totalLabor = personnelCost + ownerComp;
                        value = analysis.revenue > 0 ? (totalLabor / analysis.revenue * 100) : 0;
                      } else {
                        value = analysis[item.valueKey] || 0;
                      }
                      const avg = comparison.benchmark[item.avgKey] || 0;
                      const best = comparison.benchmark[item.bestKey] || 0;
                      const max = Math.max(value, avg, best, 1) * 1.15;
                      
                      const isGood = item.lowerIsBetter ? value <= avg : value >= avg;
                      const isBest = item.lowerIsBetter ? value <= best : value >= best;
                      
                      return (
                        <div key={item.label}>
                          <div className="flex justify-between text-sm mb-2">
                            <span className="font-medium">{item.label}</span>
                            <span className={`tabular-nums font-semibold ${isBest ? "text-emerald-600" : isGood ? "text-blue-600" : "text-red-500"}`}>
                              {value.toFixed(1)}%
                            </span>
                          </div>
                          <div className="relative h-7 bg-slate-100 rounded-full overflow-hidden">
                            {/* Best marker */}
                            <div 
                              className="absolute top-0 bottom-0 w-0.5 bg-emerald-500 z-10"
                              style={{ left: `${(best / max) * 100}%` }}
                              title={`Best: ${best}%`}
                            />
                            {/* Average marker */}
                            <div 
                              className="absolute top-0 bottom-0 w-0.5 bg-amber-500 z-10"
                              style={{ left: `${(avg / max) * 100}%` }}
                              title={`Media: ${avg}%`}
                            />
                            {/* Value bar */}
                            <div 
                              className={`h-full rounded-full transition-all ${isBest ? "bg-emerald-500" : isGood ? "bg-blue-500" : "bg-red-400"}`}
                              style={{ width: `${(value / max) * 100}%` }}
                            />
                          </div>
                          <div className="flex justify-between text-xs text-muted-foreground mt-1">
                            <span>{item.lowerIsBetter ? "Meglio ←" : ""}</span>
                            <div className="flex gap-4">
                              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500 inline-block"></span>Media: {avg}%</span>
                              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>Best: {best}%</span>
                            </div>
                            <span>{!item.lowerIsBetter ? "→ Meglio" : ""}</span>
                          </div>
                        </div>
                      );
                    })}
                    <p className="text-xs text-muted-foreground mt-2 pt-2 border-t">
                      Fonte: ISTAT 2025 (dati 2022). Best = benchmark operativo di studio.
                    </p>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">
                    {!comparison?.benchmark || Object.keys(comparison.benchmark).length === 0
                      ? "Nessun benchmark disponibile per il settore di questo cliente"
                      : "Carica dati per vedere il confronto benchmark"}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* YTD Comparison */}
          {comparison?.ytd_current && (
            <Card>
              <CardHeader>
                <CardTitle>Progressivo Anno (YTD)</CardTitle>
                <CardDescription>
                  Gen-{getMonthName(selectedMonth)} {selectedYear} vs Gen-{getMonthName(selectedMonth)} {selectedYear - 1}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={[
                    { name: "Ricavi", current: comparison.ytd_current.revenue, previous: comparison.ytd_previous?.revenue || 0 },
                    { name: "MDC", current: comparison.ytd_current.margin_contribution, previous: comparison.ytd_previous?.margin_contribution || 0 },
                    { name: "EBITDA", current: comparison.ytd_current.ebitda, previous: comparison.ytd_previous?.ebitda || 0 },
                    { name: "EBIT", current: comparison.ytd_current.ebit, previous: comparison.ytd_previous?.ebit || 0 },
                    { name: "Utile", current: comparison.ytd_current.net_result, previous: comparison.ytd_previous?.net_result || 0 },
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} />
                    <RechartsTooltip formatter={(v) => formatCurrency(v)} />
                    <Legend />
                    <Bar dataKey="current" name={selectedYear.toString()} fill="#2563EB" />
                    <Bar dataKey="previous" name={(selectedYear - 1).toString()} fill="#94A3B8" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Print-only branded footer */}
      <div className="print-brand-footer" style={{display: 'none'}}>
        <div style={{borderTop: '2px solid #b48c3c', paddingTop: '8px', marginTop: '20px', textAlign: 'center'}}>
          <div style={{fontSize: '10px', fontWeight: 'bold', color: '#b48c3c', letterSpacing: '1px'}}>
            SC Dottori Commercialisti - Controllo di Gestione
          </div>
        </div>
      </div>
    </div>
  );
};


// Verification Panel Component
const VerificationPanel = ({ result, formatCurrency, onNavigate, month, year }) => {
  const v = result.verification || {};
  const a = result.analysis || {};
  const hasVerification = v.pdf_utile !== null && v.pdf_utile !== undefined;
  const utileMatch = v.utile_match;
  const calcMatch = v.utile_calc_match;
  const overallMatch = utileMatch || calcMatch;
  const monthNames = ['','Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];

  return (
    <Card data-testid="verification-panel" className={`border-2 ${overallMatch ? 'border-green-400 bg-green-50/50' : hasVerification ? 'border-amber-400 bg-amber-50/50' : 'border-blue-200 bg-blue-50/50'}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            {overallMatch ? (
              <><CheckCircle2 className="h-6 w-6 text-green-600" /> Verifica Import - Dati Attendibili</>
            ) : hasVerification ? (
              <><AlertTriangle className="h-6 w-6 text-amber-600" /> Verifica Import - Attenzione</>
            ) : (
              <><Info className="h-6 w-6 text-blue-600" /> Riepilogo Import</>
            )}
          </CardTitle>
          <span className="text-sm text-muted-foreground">{monthNames[month]} {year}</span>
        </div>
        {overallMatch && (
          <p className="text-sm text-green-700 mt-1">
            L'utile calcolato corrisponde al PDF originale. I dati importati sono verificati e attendibili.
          </p>
        )}
        {hasVerification && !overallMatch && (
          <p className="text-sm text-amber-700 mt-1">
            L'utile calcolato non corrisponde esattamente al PDF. Verificare la classificazione dei conti.
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Key Metrics */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-lg p-4 border shadow-sm">
            <p className="text-xs text-muted-foreground mb-1">Movimenti Importati</p>
            <p className="text-2xl font-bold" data-testid="verification-entries">{result.entries_count}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border shadow-sm">
            <p className="text-xs text-muted-foreground mb-1">Ricavi Analisi</p>
            <p className="text-2xl font-bold text-green-700" data-testid="verification-revenue">{formatCurrency(a.revenue)}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border shadow-sm">
            <p className="text-xs text-muted-foreground mb-1">Utile Netto</p>
            <p className={`text-2xl font-bold ${a.net_result >= 0 ? 'text-green-700' : 'text-red-700'}`} data-testid="verification-utile">
              {formatCurrency(a.net_result)}
            </p>
          </div>
        </div>

        {/* Comparison Table */}
        {hasVerification && (
          <div className="bg-white rounded-lg border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-3 font-medium">Voce</th>
                  <th className="text-right p-3 font-medium">Valore PDF</th>
                  <th className="text-right p-3 font-medium">Valore Analisi</th>
                  <th className="text-center p-3 font-medium">Esito</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t">
                  <td className="p-3 font-medium">Totale Dare (Costi)</td>
                  <td className="p-3 text-right">{v.pdf_totale_dare != null ? formatCurrency(v.pdf_totale_dare) : '—'}</td>
                  <td className="p-3 text-right">{formatCurrency(v.analysis_costs)}</td>
                  <td className="p-3 text-center">
                    <Info className="h-4 w-4 inline text-slate-400" />
                  </td>
                </tr>
                <tr className="border-t">
                  <td className="p-3 font-medium">Totale Avere (Ricavi)</td>
                  <td className="p-3 text-right">{v.pdf_totale_avere != null ? formatCurrency(v.pdf_totale_avere) : '—'}</td>
                  <td className="p-3 text-right">{formatCurrency(v.analysis_revenue)}</td>
                  <td className="p-3 text-center">
                    <Info className="h-4 w-4 inline text-slate-400" />
                  </td>
                </tr>
                <tr className="border-t bg-slate-50 font-semibold">
                  <td className="p-3">Utile di Esercizio</td>
                  <td className="p-3 text-right">{v.pdf_utile != null ? formatCurrency(v.pdf_utile) : '—'}</td>
                  <td className="p-3 text-right">{formatCurrency(v.analysis_utile)}</td>
                  <td className="p-3 text-center" data-testid="verification-utile-status">
                    {utileMatch ? (
                      <CheckCircle2 className="h-5 w-5 inline text-green-600" />
                    ) : (
                      <AlertTriangle className="h-5 w-5 inline text-amber-600" />
                    )}
                  </td>
                </tr>
                {v.utile_diff != null && v.utile_diff > 0 && (
                  <tr className="border-t text-amber-700 text-xs">
                    <td colSpan="4" className="p-3">
                      Differenza: {formatCurrency(v.utile_diff)} — Potrebbe essere dovuta alla classificazione dei conti o a voci patrimoniali incluse.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Analysis Summary */}
        <div className="bg-white rounded-lg border p-4">
          <h4 className="font-medium mb-3">Conto Economico Sintetico</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span>Ricavi</span><span className="font-medium">{formatCurrency(a.revenue)}</span></div>
            <div className="flex justify-between text-muted-foreground"><span>- Costi variabili</span><span>{formatCurrency(a.variable_costs)}</span></div>
            <div className="flex justify-between font-medium border-t pt-1"><span>Margine di contribuzione</span><span>{formatCurrency(a.margin_contribution)}</span></div>
            <div className="flex justify-between text-muted-foreground"><span>- Costi fissi</span><span>{formatCurrency(a.fixed_costs)}</span></div>
            <div className="flex justify-between font-medium border-t pt-1"><span>EBITDA</span><span>{formatCurrency(a.ebitda)}</span></div>
            <div className="flex justify-between text-muted-foreground"><span>- Ammortamenti</span><span>{formatCurrency(a.depreciation)}</span></div>
            <div className="flex justify-between text-muted-foreground"><span>- Oneri finanziari</span><span>{formatCurrency(a.financial_charges)}</span></div>
            <div className="flex justify-between text-muted-foreground"><span>- Imposte</span><span>{formatCurrency(a.taxes)}</span></div>
            <div className="flex justify-between font-bold text-base border-t pt-2">
              <span>Utile Netto</span>
              <span className={a.net_result >= 0 ? 'text-green-700' : 'text-red-700'}>{formatCurrency(a.net_result)}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button onClick={onNavigate} className="flex-1" data-testid="go-to-dashboard-btn">
            Vai alla Dashboard
          </Button>
          <Button 
            variant="outline" 
            onClick={() => {
              const input = document.getElementById('file-upload');
              if (input) { input.value = ''; }
            }}
            data-testid="upload-another-btn"
          >
            Carica un altro mese
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};


// Upload Page
const UploadPage = () => {
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(1);
  const [isPeriodic, setIsPeriodic] = useState(false);
  const [startMonth, setStartMonth] = useState(1);
  const [endMonth, setEndMonth] = useState(3);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [entries, setEntries] = useState([]);
  const [uploadResult, setUploadResult] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [checkingMappings, setCheckingMappings] = useState(false);
  const [showSPMapping, setShowSPMapping] = useState(false);
  const [spMappingData, setSpMappingData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchClient();
  }, [clientId]);

  const fetchClient = async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}`);
      setClient(res.data);
    } catch (e) {
      toast.error("Cliente non trovato");
      navigate("/clients");
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Seleziona un file");
      return;
    }

    // First check if the client has existing mappings
    setCheckingMappings(true);
    try {
      const statusRes = await axios.get(`${API}/clients/${clientId}/mappings/status`);
      if (!statusRes.data.has_mappings) {
        // No mappings - show config page first
        const formData = new FormData();
        formData.append("file", file);
        const previewRes = await axios.post(
          `${API}/clients/${clientId}/preview-upload`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        setPreviewData(previewRes.data);
        setShowConfig(true);
        setCheckingMappings(false);
        return;
      }
    } catch (e) {
      toast.error("Errore nel controllo configurazione conti");
      setCheckingMappings(false);
      return;
    }
    setCheckingMappings(false);

    // Mappings exist - proceed with normal upload
    await doUpload();
  };

  const doUpload = async () => {
    setUploading(true);
    setUploadResult(null);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      let url = `${API}/clients/${clientId}/upload?year=${year}`;
      if (isPeriodic) {
        url += `&month=${endMonth}&period_start_month=${startMonth}&period_end_month=${endMonth}`;
      } else {
        url += `&month=${month}`;
      }
      const res = await axios.post(url, formData, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadResult(res.data);
      
      if (isPeriodic) {
        toast.success(res.data.message || `Periodo distribuito su ${res.data.num_months} mesi`);
      } else {
        toast.success(`Caricati ${res.data.entries_count} movimenti`);
        // Check for unmapped SP accounts
        const spInfo = res.data.sp_mapping_info;
        if (spInfo && spInfo.unmapped && spInfo.unmapped.length > 0) {
          setSpMappingData({ unmapped: spInfo.unmapped, chartType: spInfo.chart_type });
          setShowSPMapping(true);
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel caricamento");
    } finally {
      setUploading(false);
    }
  };

  const handleConfigSave = async () => {
    // Config was saved, now proceed with the actual upload
    setShowConfig(false);
    setPreviewData(null);
    toast.success("Configurazione salvata! Procedo con l'import...");
    await doUpload();
  };

  const formatCurrency = (val) => {
    if (val === null || val === undefined) return '—';
    return val.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  };

  // If showing account config, render that instead
  if (showConfig && previewData) {
    return (
      <AccountConfigPage
        previewData={previewData}
        clientId={clientId}
        onSave={handleConfigSave}
        onCancel={() => { setShowConfig(false); setPreviewData(null); }}
      />
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center gap-2 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/clients/${clientId}`)}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-bold">Carica Bilancio - {client?.name}</h1>
      </div>

      <div className="max-w-3xl space-y-6">
        {/* Upload Form */}
        <Card>
          <CardHeader>
            <CardTitle>Carica Dati Contabili</CardTitle>
            <CardDescription>
              Carica un file CSV o Excel con i dati del bilancio. Il file deve contenere colonne: Codice, Descrizione, Importo
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Mode toggle */}
            <div className="flex gap-2 p-1 bg-slate-100 rounded-lg w-fit" data-testid="upload-mode-toggle">
              <button
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${!isPeriodic ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
                onClick={() => setIsPeriodic(false)}
                data-testid="upload-mode-monthly"
              >
                <CalendarDays className="h-4 w-4 inline mr-1.5" />Mese Singolo
              </button>
              <button
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${isPeriodic ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
                onClick={() => setIsPeriodic(true)}
                data-testid="upload-mode-periodic"
              >
                <BarChart3 className="h-4 w-4 inline mr-1.5" />Periodo Cumulativo
              </button>
            </div>

            {isPeriodic && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
                <Info className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                <div className="text-sm text-blue-800">
                  <strong>Modalità cumulativa:</strong> il file contiene dati cumulativi (es. trimestrale, semestrale).
                  Il sistema calcolerà la differenza rispetto al periodo precedente e distribuirà il risultato equamente sui mesi selezionati.
                </div>
              </div>
            )}

            <div className={`grid ${isPeriodic ? 'grid-cols-3' : 'grid-cols-2'} gap-4`}>
              <div>
                <Label>Anno</Label>
                <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
                  <SelectTrigger data-testid="upload-year-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026].map((y) => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {isPeriodic ? (
                <>
                  <div>
                    <Label>Da Mese</Label>
                    <Select value={startMonth.toString()} onValueChange={(v) => {
                      const sm = parseInt(v);
                      setStartMonth(sm);
                      if (endMonth <= sm) setEndMonth(Math.min(sm + 2, 12));
                    }}>
                      <SelectTrigger data-testid="upload-start-month-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                          <SelectItem key={m} value={m.toString()}>{getMonthName(m)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>A Mese</Label>
                    <Select value={endMonth.toString()} onValueChange={(v) => setEndMonth(parseInt(v))}>
                      <SelectTrigger data-testid="upload-end-month-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1,2,3,4,5,6,7,8,9,10,11,12].filter(m => m > startMonth).map((m) => (
                          <SelectItem key={m} value={m.toString()}>{getMonthName(m)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              ) : (
                <div>
                  <Label>Mese</Label>
                  <Select value={month.toString()} onValueChange={(v) => setMonth(parseInt(v))}>
                    <SelectTrigger data-testid="upload-month-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                        <SelectItem key={m} value={m.toString()}>{getMonthName(m)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {isPeriodic && startMonth < endMonth && (
              <div className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3">
                Periodo: <strong>{getMonthName(startMonth)} - {getMonthName(endMonth)} {year}</strong>
                {' '}({endMonth - startMonth + 1} mesi).
                {' '}I dati CE saranno distribuiti equamente su ciascun mese.
              </div>
            )}

            <div>
              <Label>File (PDF, Excel o CSV)</Label>
              <div className="mt-2 border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
                <UploadCloud className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
                <input
                  type="file"
                  accept=".pdf,.csv,.txt,.xlsx,.xls"
                  onChange={(e) => {
                    const f = e.target.files[0];
                    setFile(f);
                    setUploadResult(null);
                    setShowConfig(false);
                    // Try to infer year and month from filename
                    if (f) {
                      const fname = f.name.toLowerCase();
                      // Detect year (e.g., "2024", "2025")
                      const yearMatch = fname.match(/(20\d{2})/);
                      if (yearMatch) setYear(parseInt(yearMatch[1]));
                      // Detect month from Italian abbreviations or numbers
                      const monthMap = {gen:1,feb:2,mar:3,apr:4,mag:5,giu:6,lug:7,ago:8,set:9,ott:10,nov:11,dic:12,
                                        gennaio:1,febbraio:2,marzo:3,aprile:4,maggio:5,giugno:6,luglio:7,agosto:8,settembre:9,ottobre:10,novembre:11,dicembre:12};
                      for (const [k, v] of Object.entries(monthMap)) {
                        if (fname.includes(k)) { setMonth(v); break; }
                      }
                      // Detect month from number pattern like "_01_" or "-01-" or " 01 "
                      const monthNumMatch = fname.match(/[_\-\s](\d{1,2})[_\-\s.]/);
                      if (monthNumMatch) {
                        const mn = parseInt(monthNumMatch[1]);
                        if (mn >= 1 && mn <= 12) setMonth(mn);
                      }
                    }
                  }}
                  className="hidden"
                  id="file-upload"
                  data-testid="file-input"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <span className="text-blue-600 hover:underline">Seleziona file</span>
                  <span className="text-muted-foreground"> o trascina qui</span>
                </label>
                {file && (
                  <p className="mt-2 text-sm text-green-600">
                    <CheckCircle2 className="h-4 w-4 inline mr-1" />
                    {file.name}
                  </p>
                )}
              </div>
            </div>

            <div className="bg-slate-50 p-4 rounded-lg">
              <h4 className="font-medium mb-2">Formati supportati</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li><strong>PDF</strong> - Bilancio esportato dal software di contabilità</li>
                <li><strong>Excel (.xlsx)</strong> - Foglio con codice conto, descrizione e importo</li>
                <li><strong>CSV</strong> - File con colonne: Codice; Descrizione; Saldo finale</li>
              </ul>
            </div>

            <Button 
              onClick={handleUpload} 
              disabled={!file || uploading || checkingMappings} 
              className="w-full"
              data-testid="upload-submit-btn"
            >
              {checkingMappings ? "Verifica configurazione conti..." : uploading ? "Caricamento..." : "Carica e Analizza"}
            </Button>
          </CardContent>
        </Card>

        {/* Verification Panel - shown after upload */}
        {uploadResult && !uploadResult.monthly_distribution && (
          <VerificationPanel 
            result={uploadResult} 
            formatCurrency={formatCurrency}
            onNavigate={() => navigate(`/clients/${clientId}`)}
            month={month}
            year={year}
          />
        )}

        {/* Periodic Upload Result */}
        {uploadResult && uploadResult.monthly_distribution && (
          <Card data-testid="periodic-upload-result">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                Distribuzione Completata
              </CardTitle>
              <CardDescription>{uploadResult.message}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold">{uploadResult.num_months}</div>
                  <div className="text-xs text-muted-foreground">Mesi distribuiti</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold">{uploadResult.cumulative_entries_count}</div>
                  <div className="text-xs text-muted-foreground">Voci cumulate</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold">{uploadResult.delta_entries_count}</div>
                  <div className="text-xs text-muted-foreground">Voci delta</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold">{uploadResult.has_previous_period ? 'Si' : 'No'}</div>
                  <div className="text-xs text-muted-foreground">Periodo precedente</div>
                </div>
              </div>

              {uploadResult.analysis && (
                <div className="border rounded-lg p-4 space-y-2">
                  <h4 className="font-medium text-sm">Riepilogo Delta (totale periodo)</h4>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div>
                      <span className="text-muted-foreground">Ricavi:</span>
                      <span className="font-semibold ml-2">{formatCurrency(uploadResult.analysis.revenue)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">EBITDA:</span>
                      <span className="font-semibold ml-2">{formatCurrency(uploadResult.analysis.ebitda)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Risultato:</span>
                      <span className="font-semibold ml-2">{formatCurrency(uploadResult.analysis.net_result)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Verification panel */}
              {uploadResult.verification && (
                <div className={`border rounded-lg p-4 space-y-2 ${uploadResult.verification.match ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`} data-testid="periodic-verification">
                  <h4 className="font-medium text-sm flex items-center gap-2">
                    {uploadResult.verification.match ? (
                      <><CheckCircle2 className="h-4 w-4 text-green-600" />Verifica Risultato</>
                    ) : (
                      <><AlertTriangle className="h-4 w-4 text-amber-600" />Verifica Risultato - Differenza Rilevata</>
                    )}
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-muted-foreground">Totale 04.xx (ricavi contabili):</span>
                      <span className="font-semibold ml-2">{formatCurrency(uploadResult.verification.raw_revenue)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Totale 03.xx (costi contabili):</span>
                      <span className="font-semibold ml-2">{formatCurrency(uploadResult.verification.raw_costs)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Utile contabile (04-03):</span>
                      <span className="font-bold ml-2">{formatCurrency(uploadResult.verification.raw_profit)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Risultato analisi:</span>
                      <span className="font-bold ml-2">{formatCurrency(uploadResult.verification.classified_net)}</span>
                    </div>
                  </div>
                  {!uploadResult.verification.match && (
                    <p className="text-xs text-amber-700 mt-1">
                      Differenza di {formatCurrency(Math.abs(uploadResult.verification.difference))} — verifica le classificazioni dei conti.
                    </p>
                  )}
                </div>
              )}

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Mese</TableHead>
                    <TableHead className="text-right">Voci</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {uploadResult.monthly_distribution.map((md) => (
                    <TableRow key={md.month}>
                      <TableCell className="font-medium">{getMonthName(md.month)}</TableCell>
                      <TableCell className="text-right">{md.entries_count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Button onClick={() => navigate(`/clients/${clientId}`)} className="w-full" data-testid="periodic-go-to-client">
                Vai al Cliente
              </Button>
            </CardContent>
          </Card>
        )}

        {/* SP Account Mapping Dialog */}
        <SPMappingDialog
          open={showSPMapping}
          onClose={() => setShowSPMapping(false)}
          unmappedAccounts={spMappingData?.unmapped || []}
          chartType={spMappingData?.chartType || 'GENERIC'}
          onSaved={() => {
            setShowSPMapping(false);
            toast.success('Mappature SP salvate! Saranno usate per tutti i futuri import.');
          }}
        />
      </div>
    </div>
  );
};

// Account Mappings Page
const MappingsPage = () => {
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const categories = [
    { value: "revenue", label: "Ricavi", color: "bg-green-100 text-green-700" },
    { value: "variable_cost", label: "Costo Variabile", color: "bg-orange-100 text-orange-700" },
    { value: "fixed_cost", label: "Costo Fisso", color: "bg-red-100 text-red-700" },
    { value: "depreciation", label: "Ammortamento", color: "bg-purple-100 text-purple-700" },
    { value: "financial", label: "Onere Finanziario", color: "bg-indigo-100 text-indigo-700" },
    { value: "tax", label: "Imposta", color: "bg-slate-100 text-slate-700" },
    { value: "admin_compensation", label: "Compenso Amministratore", color: "bg-violet-100 text-violet-700" },
    { value: "brand_royalties", label: "Royalties sul Marchio", color: "bg-violet-100 text-violet-700" },
    { value: "exclude", label: "Escludi (macroclasse)", color: "bg-gray-200 text-gray-500 line-through" },
  ];

  useEffect(() => {
    fetchData();
  }, [clientId]);

  const fetchData = async () => {
    try {
      const [clientRes, mappingsRes, periodsRes] = await Promise.all([
        axios.get(`${API}/clients/${clientId}`),
        axios.get(`${API}/clients/${clientId}/mappings`),
        axios.get(`${API}/clients/${clientId}/periods`)
      ]);
      setClient(clientRes.data);
      
      // Extract all unique accounts from periods
      const accounts = new Map();
      periodsRes.data.forEach(period => {
        period.entries?.forEach(entry => {
          if (!accounts.has(entry.account_code)) {
            accounts.set(entry.account_code, {
              account_code: entry.account_code,
              account_name: entry.account_name
            });
          }
        });
      });
      setAllAccounts(Array.from(accounts.values()));
      
      // Merge existing mappings
      const mappingMap = new Map(mappingsRes.data.map(m => [m.account_code, m]));
      const merged = Array.from(accounts.values()).map(acc => ({
        ...acc,
        category: mappingMap.get(acc.account_code)?.category || "uncategorized",
        subcategory: mappingMap.get(acc.account_code)?.subcategory || ""
      }));
      setMappings(merged);
    } catch (e) {
      toast.error("Errore nel caricamento");
    }
  };

  const updateCategory = (code, category) => {
    setMappings(prev => prev.map(m => 
      m.account_code === code ? { ...m, category, subcategory: category !== 'fixed_cost' ? '' : m.subcategory } : m
    ));
  };

  const updateSubcategory = (code, subcategory) => {
    setMappings(prev => prev.map(m => 
      m.account_code === code ? { ...m, subcategory } : m
    ));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const toSave = mappings.filter(m => m.category !== "uncategorized");
      await axios.post(`${API}/clients/${clientId}/mappings`, { mappings: toSave });
      // Recalculate all periods with new mappings
      await axios.post(`${API}/clients/${clientId}/recalculate`);
      toast.success("Mappatura salvata e analisi ricalcolate");
      navigate(`/clients/${clientId}`);
    } catch (e) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  // Auto-classify based on account code patterns
  const autoClassify = () => {
    setMappings(prev => prev.map(m => {
      const code = m.account_code;
      let category = m.category;
      let subcategory = m.subcategory || '';
      
      // Revenue accounts (47.xx, 53.xx)
      if (code.startsWith("47.") || code.startsWith("53.")) {
        category = "revenue";
        subcategory = '';
      }
      // Variable costs (55.xx - purchases, 57.01 - production services, 57.03 - transport)
      else if (code.startsWith("55.") || code.startsWith("57.01") || code.startsWith("57.03")) {
        category = "variable_cost";
        subcategory = '';
      }
      // Fixed costs with subcategories
      else if (code.startsWith("67.")) {
        category = "fixed_cost";
        subcategory = "personnel";
      }
      else if (code.startsWith("65.01") || code.startsWith("65.03")) {
        category = "fixed_cost";
        subcategory = "rent";
      }
      else if (code.startsWith("65.05") || code.startsWith("61.")) {
        category = "fixed_cost";
        subcategory = "leasing";
      }
      else if (code.startsWith("65.07") || code.startsWith("57.11")) {
        category = "fixed_cost";
        subcategory = "equipment";
      }
      else if (code.startsWith("57.13")) {
        category = "fixed_cost";
        subcategory = "maintenance";
      }
      else if (code.startsWith("57.09.13") || code.startsWith("57.09.15") || code.startsWith("57.09.17")) {
        category = "fixed_cost";
        subcategory = "utilities";
      }
      else if (code.startsWith("57.09.01") || code.startsWith("57.09.03") || code.startsWith("57.09.05") || code.startsWith("57.09.07") || code.startsWith("57.09.09") || code.startsWith("57.09.11") || code.startsWith("71.03")) {
        category = "fixed_cost";
        subcategory = "admin";
      }
      else if (code.startsWith("63.01")) {
        category = "fixed_cost";
        subcategory = "marketing";
      }
      else if (code.startsWith("63.03") || code.startsWith("59.")) {
        category = "fixed_cost";
        subcategory = "consulting";
      }
      else if (code.startsWith("63.07")) {
        category = "fixed_cost";
        subcategory = "travel";
      }
      else if (code.startsWith("63.09") || code.startsWith("71.01.01")) {
        category = "fixed_cost";
        subcategory = "postal";
      }
      else if (code.startsWith("63.05.13") || code.startsWith("63.05.15")) {
        category = "fixed_cost";
        subcategory = "insurance";
      }
      else if (code.startsWith("63.05.17")) {
        category = "fixed_cost";
        subcategory = "security";
      }
      else if (code.startsWith("63.11")) {
        category = "fixed_cost";
        subcategory = "contributions";
      }
      else if (code.startsWith("71.01.51") || code.startsWith("71.01.53") || code.startsWith("71.01")) {
        category = "fixed_cost";
        subcategory = "taxes";
      }
      else if (code.startsWith("57.09") || code.startsWith("63.05") || code.startsWith("63.") || code.startsWith("65.") || code.startsWith("71.")) {
        category = "fixed_cost";
        subcategory = "other";
      }
      // Depreciation (80.xx)
      else if (code.startsWith("80.")) {
        category = "depreciation";
        subcategory = '';
      }
      // Financial (75.xx)
      else if (code.startsWith("75.")) {
        category = "financial";
        subcategory = '';
      }
      // Tax (84.xx)
      else if (code.startsWith("84.")) {
        category = "tax";
        subcategory = '';
      }
      
      return { ...m, category, subcategory };
    }));
    toast.success("Classificazione automatica applicata");
  };

  const handleReset = async () => {
    if (!window.confirm("Sei sicuro? Questo cancellerà tutta la configurazione conti. Al prossimo upload dovrai riconfigurare i conti.")) return;
    try {
      await axios.delete(`${API}/clients/${clientId}/mappings`);
      toast.success("Configurazione conti resettata");
      navigate(`/clients/${clientId}/upload`);
    } catch (e) {
      toast.error("Errore nel reset");
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/clients/${clientId}`)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Configurazione Conti</h1>
            <p className="text-muted-foreground">{client?.name}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="destructive" size="sm" onClick={handleReset} data-testid="reset-mappings-btn">
            <RotateCcw className="h-4 w-4 mr-2" /> Reset Configurazione
          </Button>
          <Button variant="outline" onClick={autoClassify} data-testid="auto-classify-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Auto-classifica
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-mappings-btn">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Salvataggio..." : "Salva"}
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-28">Codice</TableHead>
                <TableHead>Descrizione</TableHead>
                <TableHead className="w-44">Categoria</TableHead>
                <TableHead className="w-52">Sottocategoria</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mappings.map((m) => (
                <TableRow key={m.account_code} data-testid={`mapping-row-${m.account_code}`}>
                  <TableCell className="font-mono text-sm">{m.account_code}</TableCell>
                  <TableCell>{m.account_name}</TableCell>
                  <TableCell>
                    <Select 
                      value={m.category} 
                      onValueChange={(v) => updateCategory(m.account_code, v)}
                    >
                      <SelectTrigger className="w-40" data-testid={`category-select-${m.account_code}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="uncategorized">-- Non classificato --</SelectItem>
                        {categories.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    {m.category === 'fixed_cost' && (
                      <Select 
                        value={m.subcategory || ''} 
                        onValueChange={(v) => updateSubcategory(m.account_code, v)}
                      >
                        <SelectTrigger className="w-48" data-testid={`subcategory-select-${m.account_code}`}>
                          <SelectValue placeholder="Seleziona..." />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(FIXED_COST_SUBCATEGORIES).map(([key, val]) => (
                            <SelectItem key={key} value={key}>
                              {val.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

// Benchmarks Page
const BenchmarksPage = () => {
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMatrix = async () => {
      try {
        const res = await axios.get(`${API}/sector-benchmarks`);
        setMatrix(res.data);
      } catch (e) {
        toast.error("Errore nel caricamento benchmark");
      } finally {
        setLoading(false);
      }
    };
    fetchMatrix();
  }, []);

  if (loading) return <div className="p-8 text-center">Caricamento...</div>;
  if (!matrix) return null;

  const sectors = Object.entries(matrix.sectors);
  const metrics = [
    { key: "acquisti_pct", label: "Acquisti Beni e Servizi", lowerIsBetter: true },
    { key: "personale_pct", label: "Costo del Personale", lowerIsBetter: true },
    { key: "ebitda_pct", label: "EBITDA / MOL", lowerIsBetter: false },
  ];

  return (
    <div className="p-8" data-testid="benchmarks-page">
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold tracking-tight">Benchmark Settoriali Italia</h1>
        <p className="text-muted-foreground mt-1 max-w-3xl">{matrix.disclaimer}</p>
        <p className="text-xs text-muted-foreground mt-2">Fonte: {matrix.source}</p>
      </div>

      {/* Methodology Card */}
      <Card className="mb-6 border-blue-200 bg-blue-50/50">
        <CardContent className="pt-5 pb-4">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">Metodologia di calcolo (dati ISTAT)</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-blue-800">
            <div className="bg-white/60 rounded p-2.5 border border-blue-100">
              <div className="font-semibold mb-1">Acquisti beni/servizi %</div>
              <div>= 1 - integrazione verticale</div>
            </div>
            <div className="bg-white/60 rounded p-2.5 border border-blue-100">
              <div className="font-semibold mb-1">Costo personale %</div>
              <div>= integr. verticale x (1 - redd. lorda)</div>
            </div>
            <div className="bg-white/60 rounded p-2.5 border border-blue-100">
              <div className="font-semibold mb-1">EBITDA / MOL %</div>
              <div>= integr. verticale x redditività lorda</div>
            </div>
          </div>
          <p className="text-[11px] text-blue-600 mt-2">
            Integrazione verticale = valore aggiunto / fatturato. Redditività lorda = MOL / valore aggiunto.
          </p>
        </CardContent>
      </Card>

      {/* Matrix Table */}
      <Card className="mb-8">
        <CardContent className="pt-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse" data-testid="benchmark-matrix-table">
              <thead>
                <tr className="bg-slate-100 border-b-2">
                  <th className="text-left px-3 py-2.5 min-w-[200px]">Settore</th>
                  {metrics.map(m => (
                    <th key={m.key} className="text-center px-2 py-2.5" colSpan={2}>
                      <div className="font-semibold text-xs">{m.label}</div>
                      <div className="text-[10px] text-slate-400 mt-0.5">% sul fatturato</div>
                      <div className="flex text-[10px] mt-0.5 justify-center gap-4">
                        <span className="text-slate-500">Media</span>
                        <span className="text-emerald-600">Best</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sectors.map(([sector, d]) => (
                  <tr key={sector} className="border-b hover:bg-slate-50 transition-colors">
                    <td className="px-3 py-2.5 font-medium text-sm">{sector}</td>
                    {metrics.map(m => {
                      const avg = d[`${m.key}_avg`];
                      const best = d[`${m.key}_best`];
                      return [
                        <td key={`${m.key}-avg`} className="text-center px-2 py-2.5 font-semibold tabular-nums text-sm">
                          {avg != null ? `${avg}%` : "-"}
                        </td>,
                        <td key={`${m.key}-best`} className="text-center px-2 py-2.5 text-emerald-600 font-medium tabular-nums text-sm">
                          {best != null ? `${m.lowerIsBetter ? "< " : "> "}${best}%` : "-"}
                        </td>,
                      ];
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Sector Detail Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {sectors.map(([sector, d]) => (
          <Card key={sector} data-testid={`benchmark-card-${sector}`}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{sector}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {metrics.map(m => {
                  const avg = d[`${m.key}_avg`];
                  const best = d[`${m.key}_best`];
                  if (avg == null) return null;
                  const maxBar = Math.max(avg, best) * 1.15;
                  return (
                    <div key={m.key}>
                      <div className="flex justify-between text-xs text-muted-foreground mb-1">
                        <span>{m.label}</span>
                        <span className="tabular-nums">
                          Media <span className="font-semibold text-foreground">{avg}%</span>
                          {" "}· Best <span className="font-semibold text-emerald-600">{m.lowerIsBetter ? "<" : ">"}{best}%</span>
                        </span>
                      </div>
                      <div className="relative h-5 bg-slate-100 rounded-full overflow-hidden">
                        {/* Best marker */}
                        <div 
                          className="absolute top-0 bottom-0 w-0.5 bg-emerald-500 z-10"
                          style={{ left: `${(best / maxBar) * 100}%` }}
                        />
                        {/* Average bar */}
                        <div 
                          className="h-full rounded-full bg-slate-400"
                          style={{ width: `${(avg / maxBar) * 100}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              {d.note && (
                <div className="mt-3 p-2.5 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700 leading-relaxed">
                  <AlertTriangle className="h-3 w-3 inline mr-1 flex-shrink-0" />
                  {d.note}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Best Definition */}
      <Card className="mt-8 border-emerald-200 bg-emerald-50/50">
        <CardContent className="pt-5 pb-4">
          <h3 className="text-sm font-semibold text-emerald-900 mb-2">Definizione di "Best" operativo</h3>
          <p className="text-xs text-emerald-800 leading-relaxed">
            Non "costo più basso", ma <span className="font-semibold">struttura costi migliore della media con EBITDA almeno superiore alla media di settore</span>.
            Su acquisti beni/servizi: almeno 5-10% meglio della media. Su personale: in linea o meglio della media, ma con EBITDA superiore.
            Su EBITDA: almeno 20-30% sopra la media. Questo criterio non è una statistica ISTAT, è una regola professionale prudente per trasformare la media in benchmark manageriale.
          </p>
          <p className="text-[11px] text-emerald-600 mt-2">
            Per il "best" statistico vero (quartili) occorre una banca dati tipo AIDA / Cerved / Leanus, con estrazione per ATECO + fascia ricavi.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children, viewerOnly = false, adminOnly = false }) => {
  const { token, user, loading } = useAuth();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Caricamento...</div>;
  }

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  const role = user?.role || "admin";
  if (adminOnly && role !== "admin") {
    return <Navigate to="/viewer" replace />;
  }
  // For non-explicit-viewer admin pages, viewers get redirected to the cruscotto
  // EXCEPT for `/clients/:id` which is shared (with filtered nav).
  if (!viewerOnly && role === "client_viewer" && !window.location.pathname.startsWith("/clients/")) {
    return <Navigate to="/viewer" replace />;
  }

  if (viewerOnly) {
    // Viewer landing page renders WITHOUT the admin sidebar layout.
    return <>{children}</>;
  }
  return <AppLayout>{children}</AppLayout>;
};

// Viewer cruscotto wrapper that injects the user + logout
const ViewerHomeRoute = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = () => { logout(); navigate("/login"); };
  return <ViewerHome user={user} onLogout={handleLogout} />;
};

// App
function App() {
  return (
    <AuthProvider>
      <TooltipProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/viewer" element={<ProtectedRoute viewerOnly><ViewerHomeRoute /></ProtectedRoute>} />
            <Route path="/viewers" element={<ProtectedRoute adminOnly><ViewerManagementPage /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute adminOnly><Dashboard /></ProtectedRoute>} />
            <Route path="/clients" element={<ProtectedRoute adminOnly><ClientList /></ProtectedRoute>} />
            <Route path="/clients/new" element={<ProtectedRoute adminOnly><ClientList /></ProtectedRoute>} />
            <Route path="/clients/:clientId" element={<ProtectedRoute><ClientDetail /></ProtectedRoute>} />
            <Route path="/clients/:clientId/upload" element={<ProtectedRoute adminOnly><UploadPage /></ProtectedRoute>} />
            <Route path="/clients/:clientId/mappings" element={<ProtectedRoute adminOnly><MappingsPage /></ProtectedRoute>} />
            <Route path="/benchmarks" element={<ProtectedRoute adminOnly><BenchmarksPage /></ProtectedRoute>} />
            <Route path="/" element={<RoleBasedHomeRedirect />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  );
}

const RoleBasedHomeRedirect = () => {
  const { token, user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center">Caricamento...</div>;
  if (!token) return <Navigate to="/login" replace />;
  return <Navigate to={(user?.role || "admin") === "client_viewer" ? "/viewer" : "/dashboard"} replace />;
};

export default App;
